<template>
  <div class="setup-container" aria-label="Core Configuration Setup Page">
    <v-btn id="button-save-setup" v-if="showStickySaveButton" color="success" size="large" variant="elevated"
      @click="Update" :loading="dialog" :disabled="dialog" class="save-action-button sticky-save-btn mr-3" rounded="lg">
      <v-img :src="newSaveImg" alt="Save Configuration" class="image-icon-style"></v-img>
      {{ $t('save') }} Configuration
    </v-btn>
    <v-container fluid>

      <!-- Configuration Cards -->
      <div class="config-grid">

        <v-row no-gutters class="mb-4" align="center">
          <v-col cols="12" md="6">
            <h1 style="font-weight: 600; font-size: 28px;" class="mr-4">Core Configurations</h1>
          </v-col>
          <v-col cols="12" md="6" class="d-flex flex-wrap justify-end gap-2">

            <v-btn color="success" size="large" variant="elevated" @click="Update" :loading="dialog" :disabled="dialog"
              class="save-action-button">
              <template v-slot:prepend>
                <v-img :src="newSaveImg" alt="Reprocess User Dialog" class="image-icon-style"></v-img>
              </template>
              {{ $t('save') }} Configuration
            </v-btn>

          </v-col>
        </v-row>

        <!-- GPT Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.gptConfig }]" elevation="0"
          aria-label="GPT Configuration Section">
          <div class="expandable-card-header" @click="expandedCards.gptConfig = !expandedCards.gptConfig" role="button"
            :aria-expanded="expandedCards.gptConfig" aria-controls="gpt-config-content" tabindex="0"
            @keydown.enter="expandedCards.gptConfig = !expandedCards.gptConfig"
            @keydown.space.prevent="expandedCards.gptConfig = !expandedCards.gptConfig">
            <div class="header-left">
              <img :src="imagesIcon.gptConfigIcon" alt="GPT Config" class="header-icon" />
              <span class="header-title-core-config">GPT Configuration</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle GPT Configuration">
              <v-icon size="35">{{ expandedCards.gptConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.gptConfig" id="gpt-config-content" class="card-content mt-3">

              <!-- Azure OpenAI Endpoints Section -->
              <div class="gpt-config-section">
                <div class="d-flex align-center justify-space-between mb-4">
                  <div class="storage-section-header">
                    <img :src="imagesGPTConfigCard.azureOpenAIIcon" alt="Azure OpenAI Endpoints"
                      class="storage-section-icon" />
                    <span class="storage-section-title">Azure OpenAI Endpoints</span>
                  </div>
                  <!-- Add Endpoint Button -->
                  <v-btn outlined @click="Add()" class="add-endpoint-btn"
                    v-show="setupListAzureOpenAIEndpoint.length > 0">
                    <v-icon location="left">mdi-plus</v-icon>
                    {{ "Add Endpoint" }}
                  </v-btn>
                </div>

                <div v-for="(item, index) in setupListAzureOpenAIEndpoint" :key="item.id" class="endpoint-card">
                  <v-row no-gutters>
                    <v-col cols="12" md="2">
                      <div class="entra-field-label">
                        Priority <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>Set the ranking of this item to determine its order of handling within the system
                            queue.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.priorityDisplay" :rules="[rules.required]" density="comfortable"
                        variant="outlined" @keypress="isNumberpositive($event)" class="priority-field"
                        placeholder="Enter priority">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="px-2">
                      <div class="entra-field-label">
                        URL Config <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>Manage the collection of all necessary external and internal web addresses (URLs) for
                            the
                            system's integrations and resources.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.azureOpenAIUrl" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="https://your-resource.openai.azure.com"
                        @blur="NormalizeAzureOpenAIUrl(index)"
                        @paste="$nextTick(() => NormalizeAzureOpenAIUrl(index))">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4" class="px-2">
                      <div class="entra-field-label">
                        API Key <span style="color: #C32E0E;">*</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="small" class="ml-1"
                              color="grey">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the unique key that grants your application permission to access the language
                            services
                            API.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.azureOpenAIAPIKey" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.showValue ? 'text' : 'password'" placeholder="Enter API key">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.showValue = !item.showValue">
                            <v-icon size="small">{{
                              item.showValue ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="2" class="d-flex align-center justify-end" style="padding-top: 24px;">
                      <v-btn v-if="index !== 0" icon color="error" variant="text" density="comfortable"
                        @click="Delete(index)">
                        <v-img :src="deleteIcon" width="20" height="20" />
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Embedding Configuration Section -->
              <hr color="#C9C9C9" class="my-6" />
              <div class="gpt-config-section">
                <div class="storage-section-header mb-4">
                  <img :src="imagesGPTConfigCard.embeddingConfigIcon" alt="Embedding" class="storage-section-icon" />
                  <span class="storage-section-title">Embedding Configuration</span>
                </div>

                <v-row class="pb-0">
                  <v-col cols="12" sm="12" md="4">
                    <div class="sync-sso-card" aria-label="Embedding Configuration toggle"
                      :style="setup.EmbLoadBalanceMode == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                      <div class="sync-sso-header">
                        <span class="sync-sso-title">Embedding Configuration</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Embedding Configuration information">mdi-information-outline</v-icon>
                          </template>
                          <span>Manage the distribution of embedding API traffic across available resources to maintain consistent performance and avoid server overload.</span>
                        </v-tooltip>
                      </div>
                      <div class="sync-sso-content">
                        <span class="sync-sso-label">Embedding Load Balance : {{ setup.EmbLoadBalanceMode == '0' ? 'Off' : 'On' }}</span>
                        <v-switch v-model="setup.EmbLoadBalanceMode" true-value="1" false-value="0" hide-details inset
                          density="compact"
                          :class="['sso-toggle', setup.EmbLoadBalanceMode == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                          aria-label="Toggle Embedding Configuration"></v-switch>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="12" md="4" class="pb-0">
                    <div class="entra-field-label">
                      Embedding API Version <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the API version used for all calls to the embedding model endpoint.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_APIVersion" :rules="[rules.required]"
                      density="comfortable" variant="outlined" placeholder="2023-05-15">
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" sm="12" md="4" class="pb-0">
                    <div class="entra-field-label">
                      Embedding Type <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>The classification of the embedding model based on its input data modality (text, image,
                          audio,
                          etc.).</span>
                      </v-tooltip>
                    </div>
                    <v-autocomplete id="embedingType-setup" :items="azureOpenAI_EmbedingType_List" item-title="text"
                      item-value="value" v-model="setup.AzureOpenAI_EmbedingType" :rules="[rules.required]"
                      density="comfortable" variant="outlined" placeholder="Select embedding type">
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <v-row v-show="setup.EmbLoadBalanceMode == '0'">
                  <!-- setupListAzureOpenAIEndpoint.length > 0 -->
                  <v-col cols="12" md="6" class="pb-0">
                    <div class="entra-field-label">
                      Embedding URL <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the network address required to connect to the external embedding model
                          service.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_Url" :rules="[rules.required]"
                      density="comfortable" variant="outlined"
                      placeholder="https://your-embedding-resource.openai.azure.com">
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6" class="mt-0">
                    <div class="entra-field-label">
                      Embedding API Key <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>The secret key required to authenticate your system and authorize all requests made to the
                          external embedding model for generating numerical vectors.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.AzureOpenAIEmbeding_APIKey" :rules="[rules.required]"
                      density="comfortable" variant="outlined"
                      :type="setup.Show_AzureOpenAI_APIKey ? 'text' : 'password'" placeholder="Enter embedding API key">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureOpenAI_APIKey =
                          !setup.Show_AzureOpenAI_APIKey
                          ">
                          <v-icon small>{{
                            setup.Show_AzureOpenAI_APIKey
                              ? "mdi-eye-outline" : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- GPT Mode Configuration Section -->
              <hr color="#C9C9C9" class="mb-6" />
              <div class="gpt-config-section">
                <div class="storage-section-header mb-4">
                  <img :src="imagesGPTConfigCard.gptConfigIcon" alt="GPT Mode Configuration"
                    class="storage-section-icon" />
                  <span class="storage-section-title">GPT Mode Configuration</span>
                </div>

                <v-row>
                  <!-- GPT Mode Switch Card -->
                  <v-col cols="12" md="4">
                    <div class="sync-sso-card" aria-label="GPT Mode toggle"
                      :style="setup.GPTMode == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                      <div class="sync-sso-header">
                        <span class="sync-sso-title">GPT Mode</span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="GPT Mode information">mdi-information-outline</v-icon>
                          </template>
                          <span>The identifier for the specific Generative Pre-trained Transformer (GPT) model to be
                            used for
                            API requests.</span>
                        </v-tooltip>
                      </div>
                      <div class="sync-sso-content">
                        <span class="sync-sso-label">GPT Mode : {{ setup.GPTMode == '0' ? 'Priority' : 'Fast' }}</span>
                        <v-switch v-model="setup.GPTMode" true-value="1" false-value="0" hide-details inset
                          density="compact"
                          :class="['sso-toggle', setup.GPTMode == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                          aria-label="Toggle GPT Mode"></v-switch>
                      </div>
                    </div>
                  </v-col>
                  <!-- Repeat Time Field -->
                  <v-col cols="12" md="4" v-show="setup.GPTMode == '1'">
                    <div class="entra-field-label">
                      Repeat Time (Minutes) <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the interval (in minutes) for the automated recurrence of this scheduled
                          task.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.GPTRepeatTime" @keypress="isNumber($event)" required
                      density="comfortable" variant="outlined" :rules="[rules.required]"
                      placeholder="Enter repeat time">
                    </v-text-field>
                  </v-col>
                  <!-- Model Field -->
                  <v-col cols="12" md="4" v-show="setup.GPTMode == '1'">
                    <div class="entra-field-label">
                      Model <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>The specific AI model being configured for use.</span>
                      </v-tooltip>
                    </div>
                    <v-autocomplete :items="DDModelShow" item-title="modelDisplay" item-value="modelName"
                      density="comfortable" variant="outlined" clearable v-model="setup.Model" :rules="[rules.required]"
                      required placeholder="Select Model">
                    </v-autocomplete>
                  </v-col>
                </v-row>

                <!-- Test of Questions Textarea -->
                <v-row v-show="setup.GPTMode == '1'" class="mt-2">
                  <v-col cols="12">
                    <div class="entra-field-label">
                      Test of Questions <span style="color: #C32E0E;">*</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="small" class="ml-1" color="grey">mdi-information-outline</v-icon>
                        </template>
                        <span>Select a personal question from the list. The answer will be used to verify your identity,
                          typically for account recovery or password resets. </span>
                      </v-tooltip>
                    </div>
                    <v-textarea id="Questions" density="comfortable" variant="outlined" counter auto-grow rows="6"
                      v-model="setup.Questions" :maxlength="1000" :rules="[rules.required]" required
                      placeholder="Enter test questions...">
                    </v-textarea>
                  </v-col>
                </v-row>

                <!-- Data Table -->
                <v-data-table v-show="setup.GPTMode == '1'" :items="listAzureEndpointFastMode" :headers="headers"
                  hide-default-footer :items-per-page="listAzureEndpointFastMode.length" class="mt-4">
                  <template v-slot:no-data>
                    <div class="d-flex align-center justify-center">No data available</div>
                  </template>
                  <template v-slot:item="{ item }">
                    <tr>
                      <td style="text-align: center">{{ item.priority }}</td>
                      <td>{{ item.azureOpenAIUrl }}</td>
                      <td>{{ item.responseTime }}</td>
                      <td>{{ item.replytext }}</td>
                    </tr>
                  </template>
                </v-data-table>
              </div>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- OpenAI Configuration Card -->
        <!-- <v-card class="config-card" elevation="2">
          <v-card-title class="card-header">
            <v-icon color="green" class="mr-3">mdi-brain</v-icon>
            <span>OpenAI Configuration</span>
          </v-card-title>
          <v-card-text class="card-content">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  label="OpenAI URL"
                  v-model="setup.openAIUrl"
                  :rules="[rules.required]"
                  outlined
                  dense
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="grey">mdi-link</v-icon>
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  label="API Key"
                  v-model="setup.openAIAPIKey"
                  :rules="[rules.required]"
                  outlined
                  dense
                  :type="setup.Show_OpenAI_APIKey ? 'text' : 'password'"
                >
                  <template v-slot:prepend-inner>
                    <v-icon small color="grey">mdi-key</v-icon>
                  </template>
                  <template v-slot:append>
                    <v-btn
                      icon
                      small
                      @click="setup.Show_OpenAI_APIKey = !setup.Show_OpenAI_APIKey"
                    >
                      <v-icon small>{{ setup.Show_OpenAI_APIKey ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card> -->

        <!-- Storage Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.storageConfig }]"
          elevation="0" aria-label="Storage Configuration Section">
          <div class="expandable-card-header" @click="expandedCards.storageConfig = !expandedCards.storageConfig"
            role="button" :aria-expanded="expandedCards.storageConfig" aria-controls="storage-config-content"
            tabindex="0" @keydown.enter="expandedCards.storageConfig = !expandedCards.storageConfig"
            @keydown.space.prevent="expandedCards.storageConfig = !expandedCards.storageConfig">
            <div class="header-left">
              <img :src="imagesIcon.storageIcon" alt="Storage Config" class="header-icon" />
              <span class="header-title-core-config">Storage Configuration</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle Storage Configuration">
              <v-icon size="35">{{ expandedCards.storageConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.storageConfig" id="storage-config-content" class="card-content mt-3">
              <!-- Azure Blob Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.AzureBlobStorageIcon" alt="Azure Blob Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">Azure Blob Storage</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Storage Connection <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>The configuration profile and credentials required to securely link to an external data
                          storage
                          service for reading, writing and retrieving files.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnection" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnection ? 'text' : 'password'" density="comfortable"
                      variant="outlined" placeholder="Enter storage connection">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnection =
                          !setup.Show_AzureStorageConnection
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnection
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Container Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>The unique name of the storage container (or bucket) within your cloud storage account
                          that holds
                          the application's files and data (blobs/objects).</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainer" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Enter container name">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- PDF Vector Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.PDFVectorStorage" alt="PDF Vector Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">PDF Vector Storage</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Storage Connection <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>Manage the connection parameters (endpoint URL, API key, index name) for the Vector Store
                          where
                          all PDF document embeddings are indexed.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnectionPDFVector" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnectionPDFVector ? 'text' : 'password'" density="comfortable"
                      variant="outlined" placeholder="Enter storage connection">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnectionPDFVector =
                          !setup.Show_AzureStorageConnectionPDFVector
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnectionPDFVector
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Container Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>The unique title assigned to this saved integration profile for easy identification and
                          management
                          within the system.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerPDFVector" :rules="[rules.required]"
                      density="comfortable" variant="outlined" placeholder="Enter container name">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Image Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.ImageStorage" alt="Image Storage" class="storage-section-icon" />
                  <span class="storage-section-title">Image Storage</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Storage Connection <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>Manage the endpoint and security keys needed to connect to the external system that hosts
                          all high
                          volume image assets.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageConnectionImage" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageConnectionImage ? 'text' : 'password'" density="comfortable"
                      variant="outlined" placeholder="Enter storage connection">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageConnectionImage =
                          !setup.Show_AzureStorageConnectionImage
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageConnectionImage
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Container Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the identifier for the dedicated storage container for all image uploads.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerImage" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Enter container name">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Speech Document Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.SpeechDocStorage" alt="Speech Document Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">Speech Document Storage</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Storage Connection <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>Manage the connection parameters for the storage bucket containing the source audio and
                          final text
                          output from the speech to document service.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureStorageSpeech" :rules="[rules.required]"
                      :type="setup.Show_AzureStorageSpeech ? 'text' : 'password'" density="comfortable"
                      variant="outlined" placeholder="Enter storage connection">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureStorageSpeech =
                          !setup.Show_AzureStorageSpeech
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureStorageSpeech
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Container Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the identifier for the dedicated storage container for all speech to document source
                          audio
                          and final transcripts.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureContainerSpeech" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Enter container name">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

              <!-- Quick Reply Icon Storage Section -->
              <div class="storage-section">
                <div class="storage-section-header">
                  <img :src="imagesStorageConfigCard.QuickReplyIconStorage" alt="Quick Reply Icon Storage"
                    class="storage-section-icon" />
                  <span class="storage-section-title">Quick Reply Icon Storage</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Storage Connection <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Storage Connection information">mdi-information-outline</v-icon>
                        </template>
                        <span>Manage the endpoint and keys needed to connect to the external system that stores the
                          quick reply
                          button icons.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureQuickReplyIconStorage" :rules="[rules.required]"
                      :type="setup.Show_AzureQuickReplyIconStorage ? 'text' : 'password'" density="comfortable"
                      variant="outlined" placeholder="Enter storage connection">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="
                          setup.Show_AzureQuickReplyIconStorage =
                          !setup.Show_AzureQuickReplyIconStorage
                          ">
                          <v-icon size="small" variant="text" density="comfortable">{{
                            setup.Show_AzureQuickReplyIconStorage
                              ? "mdi-eye-outline"
                              : "mdi-eye-off-outline"
                          }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Container Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Container Name information">mdi-information-outline</v-icon>
                        </template>
                        <span>Enter the identifier for the dedicated storage container for quick reply icon
                          files.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.azureQuickReplyIconContainer" :rules="[rules.required]"
                      density="comfortable" variant="outlined" placeholder="Enter container name">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Language Services Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.languageServices }]"
          elevation="0" aria-label="Language Services Section">
          <div class="expandable-card-header" @click="expandedCards.languageServices = !expandedCards.languageServices"
            role="button" :aria-expanded="expandedCards.languageServices" aria-controls="language-services-content"
            tabindex="0" @keydown.enter="expandedCards.languageServices = !expandedCards.languageServices"
            @keydown.space.prevent="expandedCards.languageServices = !expandedCards.languageServices">
            <div class="header-left">
              <img :src="imagesIcon.languageIcon" alt="Language Services" class="header-icon" />
              <span class="header-title-core-config">Language Services</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle Language Services">
              <v-icon size="35">{{ expandedCards.languageServices ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.languageServices" id="language-services-content" class="card-content">
              <!-- Custom Language Services Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Language Services tabs">
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 0 }]"
                  @click="activeLanguageTab = 0" role="tab" :aria-selected="activeLanguageTab === 0" tabindex="0"
                  @keydown.enter="activeLanguageTab = 0" @keydown.space.prevent="activeLanguageTab = 0">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 0 }]">
                    <img :src="imagesLanguageServiceCard.TranslatorIcon" alt="Translator" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': activeLanguageTab === 0 }" />
                  </div>
                  <span
                    :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 0 }]">Translator</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 1 }]"
                  @click="activeLanguageTab = 1" role="tab" :aria-selected="activeLanguageTab === 1" tabindex="0"
                  @keydown.enter="activeLanguageTab = 1" @keydown.space.prevent="activeLanguageTab = 1">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 1 }]">
                    <img :src="imagesLanguageServiceCard.LanguageServiceIcon" alt="Language Service"
                      class="sso-tab-icon" :class="{ 'sso-tab-icon-white': activeLanguageTab === 1 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 1 }]">Language
                    Service</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': activeLanguageTab === 2 }]"
                  @click="activeLanguageTab = 2" role="tab" :aria-selected="activeLanguageTab === 2" tabindex="0"
                  @keydown.enter="activeLanguageTab = 2" @keydown.space.prevent="activeLanguageTab = 2">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': activeLanguageTab === 2 }]">
                    <img :src="imagesLanguageServiceCard.SpeechToTextIcon" alt="Speech to Text" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': activeLanguageTab === 2 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': activeLanguageTab === 2 }]">Speech to
                    Text</span>
                </div>
              </div>

              <v-window v-model="activeLanguageTab" class="mt-4">
                <!-- Translator Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Endpoint <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Endpoint information">mdi-information-outline</v-icon>
                          </template>
                          <span>The complete API endpoint URL where the system sends all specific requests for the
                            language
                            translation service.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorEndpoint" :rules="[rules.required]"
                        density="comfortable" variant="outlined" placeholder="Enter endpoint URL">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">API Key <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="API Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the unique key that grants your application permission to access the language
                            services
                            API.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorKey" :rules="[rules.required]"
                        :type="setup.Show_LanguageTranslatorKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" placeholder="Enter API key">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_LanguageTranslatorKey =
                            !setup.Show_LanguageTranslatorKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_LanguageTranslatorKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Location <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Location information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the regional identifier for the translation service endpoint.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageTranslatorLocation" :rules="[rules.required]"
                        density="comfortable" variant="outlined" placeholder="Enter location">
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>

                <!-- Language Service Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">Language Endpoint <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Language Endpoint information">mdi-information-outline</v-icon>
                          </template>
                          <span>The complete API endpoint URL where the system sends all specific requests for the
                            language
                            translation service.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageEndpoint" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter language endpoint">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">Language Key <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Language Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the unique key that grants your application permission to access the language
                            services
                            API.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.languageKey" :rules="[rules.required]"
                        :type="setup.Show_LanguageKey ? 'text' : 'password'" density="comfortable" variant="outlined"
                        placeholder="Enter language key">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_LanguageKey = !setup.Show_LanguageKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_LanguageKey ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>

                <!-- Speech to Text Tab -->
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">Storage Connection <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Storage Connection information">mdi-information-outline</v-icon>
                          </template>
                          <span>Manage the connection parameters for the storage bucket containing the source audio and
                            final
                            text output from the Speech to Text process.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextKey" :rules="[rules.required]"
                        :type="setup.Show_SpeechToTextKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" placeholder="Enter storage connection">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_SpeechToTextKey =
                            !setup.Show_SpeechToTextKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_SpeechToTextKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">Speech to Text Region <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Speech to Text Region information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the regional identifier for the Speech-to-Text service endpoint you are connecting
                            to.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextRegion" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter region">
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Speech to Text Language <span
                            class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Speech to Text Language information">mdi-information-outline</v-icon>
                          </template>
                          <span>Select the language code corresponding to the spoken language in the audio source (e.g.,
                            en-US).</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.speechToTextLanguage" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter Speech to Text Language">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">FFmpeg Path <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="FFmpeg Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>The absolute file path on the server where the FFmpeg executable is installed. FFmpeg is
                            required for processing and converting audio/video files before transcription.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.pathFFMpeg" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter FFmpeg path">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">FFmpeg Local Path <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="FFmpeg Local Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the complete directory path to the FFmpeg binary as it exists on the local
                            operating
                            system.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.pathFFMpegLocal" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter FFmpeg local path">
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Mail Config -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.mailConfig }]" elevation="0"
          aria-label="Mail Configuration Section">
          <div class="expandable-card-header" @click="expandedCards.mailConfig = !expandedCards.mailConfig"
            role="button" :aria-expanded="expandedCards.mailConfig" aria-controls="mail-config-content" tabindex="0"
            @keydown.enter="expandedCards.mailConfig = !expandedCards.mailConfig"
            @keydown.space.prevent="expandedCards.mailConfig = !expandedCards.mailConfig">
            <div class="header-left">
              <img :src="imagesIcon.mailIcon" alt="Mail Config" class="header-icon" />
              <span class="header-title-core-config">Mail Config</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle Mail Configuration">
              <v-icon size="35">{{ expandedCards.mailConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.mailConfig" id="mail-config-content" class="card-content">
              
              <!-- Mail Provider & Method Selection -->
              <v-row class="mt-1">
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">Email Sending Method <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>Choose how to send emails: SMTP (direct server connection) or Mailgun (API-based service).</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.emailSendingMethod" :items="emailSendingMethodList" item-title="text"
                    item-value="value" density="comfortable" variant="outlined" placeholder="Select sending method">
                  </v-select>
                </v-col>

                <v-col cols="12" md="4" v-if="setup.emailSendingMethod === 'SMTP'">
                  <div class="entra-field-label">
                    <span class="field-label-text">SMTP Provider <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>Select your email provider. Office 365 requires OAuth2 authentication (Basic Auth deprecated by Microsoft).</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.smtpProvider" :items="smtpProviderList" item-title="text" item-value="value"
                    density="comfortable" variant="outlined" placeholder="Select SMTP provider">
                  </v-select>
                </v-col>

                <v-col cols="12" md="4" v-if="setup.emailSendingMethod === 'SMTP'">
                  <div class="entra-field-label">
                    <span class="field-label-text">Authentication Method</span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                      </template>
                      <span>Office 365 requires OAuth2. Gmail can use Basic Auth with App Password.</span>
                    </v-tooltip>
                  </div>
                  <v-select v-model="setup.smtpAuthMethod" :items="smtpAuthMethodList" item-title="text" item-value="value"
                    density="comfortable" variant="outlined" placeholder="Select SMTP provider" :disabled="setup.smtpProvider === 'Gmail'">
                  </v-select>
                </v-col>
              </v-row>

              <!-- SMTP Basic Settings -->
              <div v-if="setup.emailSendingMethod === 'SMTP'">
                <hr color="#C9C9C9" class="my-4" />
                <div class="storage-section-header mb-3">
                  <v-icon size="20" color="primary">mdi-server</v-icon>
                  <span class="storage-section-title">SMTP Server Settings</span>
                </div>
                <v-row>
                  <v-col cols="12" md="3">
                    <div class="entra-field-label">
                      <span class="field-label-text">SMTP Host <span class="required-asterisk">*</span></span>
                    </div>
                    <v-text-field v-model="setup.smtpHost" density="comfortable" variant="outlined"
                      :placeholder="setup.smtpProvider === 'Office365' ? 'smtp.office365.com' : 'smtp.gmail.com'"
                      :disabled="setup.smtpProvider !== 'Other'">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">SMTP Port <span class="required-asterisk">*</span></span>
                    </div>
                    <v-text-field v-model="setup.smtpPort" type="number" density="comfortable" variant="outlined"
                      placeholder="587" :disabled="setup.smtpProvider !== 'Other'">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">Enable SSL</span>
                    </div>
                    <v-switch v-model="setup.smtpEnableSsl" color="success" hide-details
                      :disabled="setup.smtpProvider !== 'Other'"
                      :class="['sso-toggle', setup.smtpEnableSsl ? 'sso-toggle--active' : 'sso-toggle--inactive']">
                    </v-switch>
                  </v-col>

                  <v-col cols="12" md="2">
                    <div class="entra-field-label">
                      <span class="field-label-text">Timeout (ms)</span>
                    </div>
                    <v-text-field v-model="setup.smtpTimeout" type="number" density="comfortable" variant="outlined"
                      placeholder="30000">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- From Email Settings -->
                <v-row>
                  <v-col cols="12" md="4">
                    <div class="entra-field-label">
                      <span class="field-label-text">Email Address (From) <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>The email address used for sending emails.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.emailHost" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="sender@example.com">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="4">
                    <div class="entra-field-label">
                      <span class="field-label-text">Display Name <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>The name displayed as the sender in recipient's inbox.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.hostName" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Your Company Name">
                    </v-text-field>
                  </v-col>
                </v-row>

                <!-- Basic Auth (Gmail / Other) -->
                <div v-if="setup.smtpAuthMethod !== 'OAuth2'">
                  <hr color="#C9C9C9" class="my-4" />
                  <div class="storage-section-header mb-3">
                    <v-icon size="20" color="primary">mdi-lock</v-icon>
                    <span class="storage-section-title">Basic Authentication</span>
                    <v-chip v-if="setup.smtpProvider === 'Gmail'" size="small" color="warning" class="ml-2">
                      Requires App Password
                    </v-chip>
                  </div>
                  <v-row>
                    <v-col cols="12" md="6">
                      <div class="entra-field-label">
                        <span class="field-label-text">Password / App Password <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>{{ setup.smtpProvider === 'Gmail' ? 'For Gmail with 2FA enabled, use an App Password generated from Google Account settings.' : 'The password for SMTP authentication.' }}</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.password" :rules="[rules.required]"
                        :type="setup.Show_Password ? 'text' : 'password'" density="comfortable" variant="outlined"
                        placeholder="Enter password">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_Password = !setup.Show_Password">
                            <v-icon size="small">{{ setup.Show_Password ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- OAuth2 Settings (Office 365) -->
                <div v-if="setup.smtpAuthMethod === 'OAuth2'">
                  <hr color="#C9C9C9" class="my-4" />
                  <div class="storage-section-header mb-3">
                    <v-icon size="20" color="primary">mdi-shield-key</v-icon>
                    <span class="storage-section-title">OAuth2 Authentication (Azure AD)</span>
                    <v-chip size="small" color="error" class="ml-2">Recommended for Office 365</v-chip>
                  </div>
                  <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                    <strong>Setup Guide:</strong> Create an App Registration in Azure AD with <code>Mail.Send</code> permission.
                    <a href="https://learn.microsoft.com/en-us/exchange/client-developer/legacy-protocols/how-to-authenticate-an-imap-pop-smtp-application-by-using-oauth" 
                       target="_blank" class="ml-1">Learn more</a>
                  </v-alert>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Tenant ID <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > Overview > Directory (tenant) ID</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2TenantId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        :type="setup.Show_SmtpOAuth2TenantId ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2TenantId = !setup.Show_SmtpOAuth2TenantId">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2TenantId ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Client ID <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > App Registration > Overview > Application (client) ID</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2ClientId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        :type="setup.Show_SmtpOAuth2ClientId ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2ClientId = !setup.Show_SmtpOAuth2ClientId">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2ClientId ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="4">
                      <div class="entra-field-label">
                        <span class="field-label-text">Client Secret <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>Azure AD > App Registration > Certificates & secrets > New client secret</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.smtpOAuth2ClientSecret" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter client secret"
                        :type="setup.Show_SmtpOAuth2ClientSecret ? 'text' : 'password'">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_SmtpOAuth2ClientSecret = !setup.Show_SmtpOAuth2ClientSecret">
                            <v-icon size="small">{{ setup.Show_SmtpOAuth2ClientSecret ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </div>

              <!-- Mailgun Settings -->
              <div v-if="setup.emailSendingMethod === 'Mailgun'">
                <hr color="#C9C9C9" class="my-4" />
                <div class="storage-section-header mb-3">
                  <v-icon size="20" color="primary">mdi-email-fast</v-icon>
                  <span class="storage-section-title">Mailgun API Settings</span>
                </div>
                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Mailgun API URL <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>The Mailgun API endpoint for your domain (e.g., https://api.mailgun.net/v3/your-domain/messages)</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailgunApiUrl" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="https://api.mailgun.net/v3/your-domain/messages">
                    </v-text-field>
                  </v-col>

                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">Mailgun API Key <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>Your Mailgun private API key from the Mailgun dashboard.</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailgunApiKey" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Enter Mailgun API key"
                      :type="setup.Show_MailgunApiKey ? 'text' : 'password'">
                      <template v-slot:append-inner>
                        <v-btn icon size="small" variant="text" density="comfortable" @click="setup.Show_MailgunApiKey = !setup.Show_MailgunApiKey">
                          <v-icon size="small">{{ setup.Show_MailgunApiKey ? "mdi-eye-outline" : "mdi-eye-off-outline" }}</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <div class="entra-field-label">
                      <span class="field-label-text">From Email <span class="required-asterisk">*</span></span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon">mdi-information-outline</v-icon>
                        </template>
                        <span>The sender email address (format: "Name &lt;email@domain.com&gt;")</span>
                      </v-tooltip>
                    </div>
                    <v-text-field v-model="setup.mailFrom" :rules="[rules.required]" density="comfortable"
                      variant="outlined" placeholder="Support <support@yourdomain.com>">
                    </v-text-field>
                  </v-col>
                </v-row>
              </div>

            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- System Settings Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.systemSettings }]"
          elevation="0" aria-label="System Settings Section">
          <div class="expandable-card-header" @click="expandedCards.systemSettings = !expandedCards.systemSettings"
            role="button" :aria-expanded="expandedCards.systemSettings" aria-controls="system-settings-content"
            tabindex="0" @keydown.enter="expandedCards.systemSettings = !expandedCards.systemSettings"
            @keydown.space.prevent="expandedCards.systemSettings = !expandedCards.systemSettings">
            <div class="header-left">
              <img :src="imagesIcon.systemIcon" alt="System Settings" class="header-icon" />
              <span class="header-title-core-config">System Settings</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle System Settings">
              <v-icon size="35">{{ expandedCards.systemSettings ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.systemSettings" id="system-settings-content" class="card-content">
              <!-- First Row: 4 Switch Cards -->
              <v-row class="mt-1">
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Auto Stream toggle"
                    :style="setup.autoStream == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">Auto Stream</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Auto Stream information">mdi-information-outline</v-icon>
                        </template>
                        <span>Toggle to automatically begin streaming the response data without waiting for the full
                          generation
                          to complete.</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">Auto Stream : {{ setup.autoStream == '1' ? 'Enabled' : 'Disabled' }}</span>
                      <v-switch v-model="setup.autoStream" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.autoStream == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Auto Stream"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Anonymous Mode toggle"
                    :style="setup.anonymous == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">Anonymous Mode</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Anonymous Mode information">mdi-information-outline</v-icon>
                        </template>
                        <span>Toggle to process all user interactions without tracking or saving identifying
                          data.</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">Anonymous Mode : {{ setup.anonymous == '1' ? 'On' : 'Off' }}</span>
                      <v-switch v-model="setup.anonymous" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.anonymous == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Anonymous Mode"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Force Disable GPT toggle"
                    :style="setup.FlagDisableGPT == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">Force Disable GPT</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Force Disable GPT information">mdi-information-outline</v-icon>
                        </template>
                        <span>Toggle to globally deactivate the GPT model integration, blocking all generative AI
                          functions.</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">Force Disable GPT : {{ setup.FlagDisableGPT == '1' ? 'On' : 'Off' }}</span>
                      <v-switch v-model="setup.FlagDisableGPT" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.FlagDisableGPT == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Force Disable GPT"></v-switch>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="sync-sso-card" aria-label="Auto Disable GPT toggle"
                    :style="setup.FlagAutoDisableGPT == '1' ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">Auto Disable GPT</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Auto Disable GPT information">mdi-information-outline</v-icon>
                        </template>
                        <span>Toggle to automatically block all GPT API requests once the specified usage or cost
                          threshold has
                          been reached.</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">Auto Disable GPT : {{ setup.FlagAutoDisableGPT == '1' ? 'On' : 'Off'
                        }}</span>
                      <v-switch v-model="setup.FlagAutoDisableGPT" true-value="1" false-value="0" hide-details inset density="compact"
                        :class="['sso-toggle', setup.FlagAutoDisableGPT == '1' ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Auto Disable GPT"></v-switch>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <!-- Second Row: Time Delay and Character Limit -->
              <v-row class="mb-4">
                <v-col cols="12" md="6">
                  <div class="entra-field-label">
                    <span class="field-label-text">Time Delay (ms) <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Time Delay information">mdi-information-outline</v-icon>
                      </template>
                      <span>Enter the pause period, in milliseconds, to be applied before continuing the
                        operation.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.TimeDelayCharacter" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" placeholder="Enter time delay">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="entra-field-label">
                    <span class="field-label-text">Character Limit <span class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Character Limit information">mdi-information-outline</v-icon>
                      </template>
                      <span>The maximum total number of characters (letters, spaces, punctuation) allowed for input in
                        this
                        field or for the system's generated output.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.LimitedCharacter" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" placeholder="Enter character limit">
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Third Row: GPT Auto-Disable Period, Pricing, and Message -->
              <v-row>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">GPT Auto-Disable Period <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Period information">mdi-information-outline</v-icon>
                      </template>
                      <span>The duration used to calculate and enforce the cost or usage limit before the GPT model is
                        automatically shut down.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTPeriodDate" @keypress="isNumber($event)"
                    density="comfortable" variant="outlined" placeholder="Enter period in days">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">GPT Auto-Disable Pricing (USD) <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Pricing information">mdi-information-outline</v-icon>
                      </template>
                      <span>Enter the dollar amount (USD) that, when reached, will automatically trigger the shutdown of
                        GPT API
                        access.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTPricing" @keypress="isNumber($event)" density="comfortable"
                    variant="outlined" placeholder="Enter pricing threshold">
                  </v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="entra-field-label">
                    <span class="field-label-text">GPT Auto-Disable Message <span
                        class="required-asterisk">*</span></span>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="GPT Auto-Disable Message information">mdi-information-outline</v-icon>
                      </template>
                      <span>The custom message displayed to the user or logged by the system when GPT access is
                        automatically
                        disabled after reaching the set usage or cost threshold.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.AutoDisableGPTMessage" density="comfortable" variant="outlined"
                    placeholder="Enter auto-disable message">
                  </v-text-field>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- other config -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.otherConfig }]" elevation="0"
          aria-label="Other Configuration Section">
          <div class="expandable-card-header" @click="expandedCards.otherConfig = !expandedCards.otherConfig"
            role="button" :aria-expanded="expandedCards.otherConfig" aria-controls="other-config-content" tabindex="0"
            @keydown.enter="expandedCards.otherConfig = !expandedCards.otherConfig"
            @keydown.space.prevent="expandedCards.otherConfig = !expandedCards.otherConfig">
            <div class="header-left">
              <img :src="imagesIcon.otherIcon" alt="Other Config" class="header-icon" />
              <span class="header-title-core-config">Other Config</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle Other Configuration">
              <v-icon size="35">{{ expandedCards.otherConfig ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.otherConfig" id="other-config-content" class="card-content">
              <!-- Custom Other Config Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Other configuration tabs">
                <div :class="['sso-tab-item', { 'sso-tab-active': otherTab === 0 }]" @click="otherTab = 0" role="tab"
                  :aria-selected="otherTab === 0" tabindex="0" @keydown.enter="otherTab = 0"
                  @keydown.space.prevent="otherTab = 0">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': otherTab === 0 }]">
                    <img :src="imagesOtherConfigCard.GoogleIcon" alt="Google Config" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': otherTab === 0 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': otherTab === 0 }]">Google Config</span>
                </div>
                <div :class="['sso-tab-item', { 'sso-tab-active': otherTab === 1 }]" @click="otherTab = 1" role="tab"
                  :aria-selected="otherTab === 1" tabindex="0" @keydown.enter="otherTab = 1"
                  @keydown.space.prevent="otherTab = 1">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': otherTab === 1 }]">
                    <img :src="imagesOtherConfigCard.AnthropicIcon" alt="Anthropic Config" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': otherTab === 1 }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': otherTab === 1 }]">Anthropic Config</span>
                </div>
              </div>

              <v-window v-model="otherTab" class="mt-4">
                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="6">
                      <!-- Project ID Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">Project ID <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Project ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>The unique, alphanumeric identifier assigned to your project in Google Cloud Platform
                            (GCP).</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.projectID" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter Project ID">
                      </v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                      <!-- Location ID Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">Location ID <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Location ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the official alphanumeric ID that uniquely identifies the desired physical
                            location or
                            data center.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.locationID" :rules="[rules.required]" density="comfortable"
                        variant="outlined" placeholder="Enter Location ID">
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" sm="12" md="12" class="d-flex justify-end align-center mb-3 mr-3">
                      <div class="d-flex flex-row align-center px-3">
                        <v-icon class="mr-2" :disabled="!isUploadFileJSON" color="green" large>
                          mdi-check-circle </v-icon>
                        <span class="font-bold">JSON Credential</span>
                        <!-- <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon ml-1" aria-label="JSON Credential information">mdi-information-outline</v-icon>
                        </template>
                        <span>Upload the JSON credential file from your Google Cloud Platform service account.</span>
                      </v-tooltip> -->
                      </div>

                      <v-tooltip location="top start" max-width="300">
                        <template #activator="{ props }">
                          <div v-bind="props">
                            <DragDropFile :dialog="true" :DragDrop="false" :ShowListFile="false"
                              :allowType="['application/json']" :accept="'application/json'"
                              @filesUploaded="uploadFile($event)" />
                          </div>
                        </template>
                        <span>Upload JSON file only</span>
                      </v-tooltip>
                      <span>{{ tempFileJSON != null && tempFileJSON != '' && tempFileJSON != undefined ?
                        tempFileJSON.name
                        :
                        '' }}</span>
                    </v-col>
                  </v-row>
                </v-window-item>

                <v-window-item>
                  <v-row class="mt-4">
                    <v-col cols="12" md="12">
                      <!-- Anthropic API Key Field with Label and Tooltip -->
                      <div class="entra-field-label">
                        <span class="field-label-text">Anthropic API Key <span class="required-asterisk">*</span></span>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Anthropic API Key information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter your Anthropic API Key to enable Claude AI integration.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="setup.amazonClaude_APIKey" :rules="[rules.required]"
                        :type="setup.Show_AmazonClaude_APIKey ? 'text' : 'password'" density="comfortable"
                        variant="outlined" placeholder="Enter Anthropic API Key">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable" @click="
                            setup.Show_AmazonClaude_APIKey =
                            !setup.Show_AmazonClaude_APIKey
                            ">
                            <v-icon size="small" variant="text" density="comfortable">{{
                              setup.Show_AmazonClaude_APIKey
                                ? "mdi-eye-outline"
                                : "mdi-eye-off-outline"
                            }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- single sign on -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.singleSignOn }]"
          elevation="0" aria-label="Single Sign-On Section">
          <div class="expandable-card-header" @click="expandedCards.singleSignOn = !expandedCards.singleSignOn"
            role="button" :aria-expanded="expandedCards.singleSignOn" aria-controls="single-sign-on-content"
            tabindex="0" @keydown.enter="expandedCards.singleSignOn = !expandedCards.singleSignOn"
            @keydown.space.prevent="expandedCards.singleSignOn = !expandedCards.singleSignOn">
            <div class="header-left">
              <img :src="imagesIcon.ssoIcon" alt="Single Sign-On" class="header-icon" />
              <span class="header-title-core-config">Single Sign-On</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn" aria-label="Toggle Single Sign-On">
              <v-icon size="35">{{ expandedCards.singleSignOn ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.singleSignOn" id="single-sign-on-content" class="card-content">
              <!-- Custom SSO Tabs with Icons -->
              <div class="sso-tabs-container" aria-label="Single Sign-On provider tabs">
                <div v-for="(item, index) in setupOpenIDConnectList" :key="index"
                  :class="['sso-tab-item', { 'sso-tab-active': signOnTab === index }]" @click="signOnTab = index"
                  role="tab" :aria-selected="signOnTab === index" tabindex="0" @keydown.enter="signOnTab = index"
                  @keydown.space.prevent="signOnTab = index">
                  <div :class="['sso-tab-icon-wrapper', { 'sso-tab-icon-active': signOnTab === index }]">
                    <img :src="imagesSSOCard[item.provider + 'Icon']" :alt="item.provider" class="sso-tab-icon"
                      :class="{ 'sso-tab-icon-white': signOnTab === index }" />
                  </div>
                  <span :class="['sso-tab-label', { 'sso-tab-label-active': signOnTab === index }]">{{ item.provider
                    }}</span>
                </div>
              </div>

              <v-window v-model="signOnTab" class="mt-4">
                <v-window-item v-for="(item, index) in setupOpenIDConnectList" :key="index">
                  <!-- SSO Switch Cards Row -->
                  <v-row class="mb-4">
                    <v-col cols="12" md="6">
                      <div class="sync-sso-card" aria-label="Single Sign-On toggle"
                        :style="item.sso ? 'background: #FFF2F2' : 'background: #EDEDED'">
                        <div class="sync-sso-header">
                          <span class="sync-sso-title">Single Sign-On {{ item.provider }}</span>
                          <v-tooltip location="top start" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                                aria-label="SSO information">mdi-information-outline</v-icon>
                            </template>
                            <span v-if="item.provider === 'Line'">Toggle to activate Line Single Sign-On ,allowing Line
                              users to
                              authenticate for registration.</span>
                            <span v-else>Toggle to enable Single Sign-On (SSO) via the {{ item.provider }} Identity
                              Platform,
                              allowing user authentication with their {{ item.provider }} account</span>
                          </v-tooltip>
                        </div>
                        <div class="sync-sso-content">
                          <span class="sync-sso-label">Single Sign-On {{ item.provider }} : {{ item.sso ? 'Open' :
                            'Close'
                            }}</span>
                          <v-switch v-model="item.sso" hide-details inset density="compact"
                            :class="['sso-toggle', item.sso ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                            :aria-label="'Toggle Single Sign-On ' + item.provider"></v-switch>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="sync-sso-card" aria-label="Single Sign-On Chat toggle"
                        :style="item.ssoChat ? 'background: #FFF2F2' : 'background: #EDEDED'">
                        <div class="sync-sso-header">
                          <span class="sync-sso-title">Single Sign-On {{ item.provider }} in Chat</span>
                          <v-tooltip location="top start" max-width="300">
                            <template v-slot:activator="{ props }">
                              <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                                aria-label="SSO Chat information">mdi-information-outline</v-icon>
                            </template>
                            <span v-if="item.provider === 'Line'">Toggle to activate Line Single Sign-On ,allowing Line
                              users to
                              authenticate for chat access without separate registration.</span>
                            <span v-else>Toggle to activate {{ item.provider }} SSO, allowing users to authenticate for
                              chat
                              access via their {{ item.provider }} account</span>
                          </v-tooltip>
                        </div>
                        <div class="sync-sso-content">
                          <span class="sync-sso-label">Single Sign-On {{ item.provider }} in Chat : {{ item.ssoChat ?
                            'Open' :
                            'Close' }}</span>
                          <v-switch v-model="item.ssoChat" hide-details inset density="compact"
                            :class="['sso-toggle', item.ssoChat ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                            :aria-label="'Toggle Single Sign-On ' + item.provider + ' in Chat'"></v-switch>
                        </div>
                      </div>
                    </v-col>
                  </v-row>

                  <!-- Text Fields Row -->
                  <v-row>
                    <!-- Instance Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Instance field">
                        <label class="field-label">Instance <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Instance information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the host URL for your running Authentik server installation.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.instance" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :disabled="item.provider == 'Microsoft'" hide-details="auto"
                        aria-label="Instance input"></v-text-field>
                    </v-col>

                    <!-- Domain Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Domain field">
                        <label class="field-label">Domain <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Domain information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the host domain name (e.g., company.com) that defines the scope of this
                            connection.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.domain" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="Domain input"></v-text-field>
                    </v-col>

                    <!-- Tenant ID Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="Tenant ID field">
                        <label class="field-label">Tenant ID <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Tenant ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the GUID that uniquely identifies your specific tenant (organizational container)
                            within
                            the cloud service.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.tenantId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_TenantId ? 'text' : 'password'" hide-details="auto"
                        aria-label="Tenant ID input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_TenantId = !item.Show_TenantId"
                            :aria-label="item.Show_TenantId ? 'Hide Tenant ID' : 'Show Tenant ID'">
                            <v-icon size="small">{{ item.Show_TenantId ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- Client ID Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="Client ID field">
                        <label class="field-label">Client ID <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Client ID information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the unique public ID assigned to your application for API or Single Sign-On
                            integration.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.clientId" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_ClientId ? 'text' : 'password'" hide-details="auto"
                        aria-label="Client ID input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_ClientId = !item.Show_ClientId"
                            :aria-label="item.Show_ClientId ? 'Hide Client ID' : 'Show Client ID'">
                            <v-icon size="small">{{ item.Show_ClientId ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- Callback Path Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik'">
                      <div class="field-label-wrapper" aria-label="Callback Path field">
                        <label class="field-label">Callback Path <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Callback Path information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the specific URL path where the external service should return the user to
                            complete the
                            authentication process.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.callbackPath" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="Callback Path input"></v-text-field>
                    </v-col>

                    <!-- Client Secret Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="Client Secret field">
                        <label class="field-label">Client Secret <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="Client Secret information">mdi-information-outline</v-icon>
                          </template>
                          <span>Enter the secret string used alongside the Client ID to securely authenticate the
                            application
                            with your Authentik instance.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.clientSecret" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_ClientSecret ? 'text' : 'password'" hide-details="auto"
                        aria-label="Client Secret input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_ClientSecret = !item.Show_ClientSecret"
                            :aria-label="item.Show_ClientSecret ? 'Hide Client Secret' : 'Show Client Secret'">
                            <v-icon size="small">{{ item.Show_ClientSecret ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- API Scope Field -->
                    <v-col cols="12" sm="6" md="6" v-show="item.provider != 'Authentik' && item.provider != 'Line'">
                      <div class="field-label-wrapper" aria-label="API Scope field">
                        <label class="field-label">API Scope <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="API Scope information">mdi-information-outline</v-icon>
                          </template>
                          <span>Defines the specific, limited set of permissions or access rights your application
                            requests from
                            the external service</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.apiScope" :rules="[rules.required]" density="comfortable"
                        variant="outlined" :type="item.Show_APIScope ? 'text' : 'password'" hide-details="auto"
                        aria-label="API Scope input">
                        <template v-slot:append-inner>
                          <v-btn icon size="small" variant="text" density="comfortable"
                            @click="item.Show_APIScope = !item.Show_APIScope"
                            :aria-label="item.Show_APIScope ? 'Hide API Scope' : 'Show API Scope'">
                            <v-icon size="small">{{ item.Show_APIScope ? "mdi-eye-outline" : "mdi-eye-off-outline"
                              }}</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </v-col>

                    <!-- URL Config Field -->
                    <v-col cols="12" sm="6" md="6">
                      <div class="field-label-wrapper" aria-label="URL Config field">
                        <label class="field-label">URL Config <span class="required-asterisk">*</span></label>
                        <v-tooltip location="top start" max-width="300">
                          <template v-slot:activator="{ props }">
                            <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                              aria-label="URL Config information">mdi-information-outline</v-icon>
                          </template>
                          <span>Manage the configuration parameters for all system-critical URLs.</span>
                        </v-tooltip>
                      </div>
                      <v-text-field v-model="item.urlConfig" :rules="[rules.required]" density="comfortable"
                        variant="outlined" hide-details="auto" aria-label="URL Config input"></v-text-field>
                    </v-col>
                  </v-row>
                </v-window-item>
              </v-window>
            </v-card-text>
          </v-expand-transition>
        </v-card>

        <!-- Microsoft Entra ID Group Configuration Card -->
        <v-card :class="['config-card-expandable', { 'config-card-expanded': expandedCards.microsoftEntra }]"
          elevation="0" aria-label="Microsoft Entra ID Group Configuration Section">
          <div class="expandable-card-header" @click="expandedCards.microsoftEntra = !expandedCards.microsoftEntra"
            role="button" :aria-expanded="expandedCards.microsoftEntra" aria-controls="microsoft-entra-content"
            tabindex="0" @keydown.enter="expandedCards.microsoftEntra = !expandedCards.microsoftEntra"
            @keydown.space.prevent="expandedCards.microsoftEntra = !expandedCards.microsoftEntra">
            <div class="header-left">
              <img :src="imagesIcon.microsoftIcon" alt="Microsoft Entra ID" class="header-icon" />
              <span class="header-title-core-config">Microsoft Entra ID Group Configuration</span>
            </div>
            <v-btn icon variant="text" size="small" class="toggle-icon-btn"
              aria-label="Toggle Microsoft Entra ID Configuration">
              <v-icon size="35">{{ expandedCards.microsoftEntra ? 'mdi-menu-up' : 'mdi-menu-down' }}</v-icon>
            </v-btn>
          </div>
          <v-expand-transition>
            <v-card-text v-show="expandedCards.microsoftEntra" id="microsoft-entra-content" class="card-content">
              <!-- Row 1: Tenant ID and Client ID -->
              <v-row>
                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Tenant ID field">
                    <label class="field-label">Tenant ID <span class="required-asterisk">*</span></label>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Tenant ID information">mdi-information-outline</v-icon>
                      </template>
                      <span>Enter the GUID of the Microsoft Entra ID tenant containing the groups that will be mapped to
                        application roles.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupTenantID" :rules="[rules.required]" density="comfortable"
                    variant="outlined" :type="setup.Show_SecurityGroupTenantID ? 'text' : 'password'"
                    hide-details="auto" aria-label="Tenant ID input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupTenantID = !setup.Show_SecurityGroupTenantID"
                        :aria-label="setup.Show_SecurityGroupTenantID ? 'Hide Tenant ID' : 'Show Tenant ID'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupTenantID ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Client ID field">
                    <label class="field-label">Client ID <span class="required-asterisk">*</span></label>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Client ID information">mdi-information-outline</v-icon>
                      </template>
                      <span>Enter the unique public ID assigned to your application for API or Single Sign-On
                        integration.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupClientID" :rules="[rules.required]" density="comfortable"
                    variant="outlined" :type="setup.Show_SecurityGroupClientID ? 'text' : 'password'"
                    hide-details="auto" aria-label="Client ID input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupClientID = !setup.Show_SecurityGroupClientID"
                        :aria-label="setup.Show_SecurityGroupClientID ? 'Hide Client ID' : 'Show Client ID'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupClientID ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>

              <!-- Row 2: Client Secret and Sync SSO Groups -->
              <v-row class="mt-4" align="end">
                <v-col cols="12" md="6">
                  <div class="field-label-wrapper" aria-label="Client Secret field">
                    <label class="field-label">Client Secret <span class="required-asterisk">*</span></label>
                    <v-tooltip location="top start" max-width="300">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                          aria-label="Client Secret information">mdi-information-outline</v-icon>
                      </template>
                      <span>Enter the secret string used the Client ID to authenticate the application and allow access
                        to Entra
                        ID group data.</span>
                    </v-tooltip>
                  </div>
                  <v-text-field v-model="setup.SecurityGroupClientSecrete" :rules="[rules.required]"
                    density="comfortable" variant="outlined"
                    :type="setup.Show_SecurityGroupClientSecrete ? 'text' : 'password'" hide-details="auto"
                    aria-label="Client Secret input">
                    <template v-slot:append-inner>
                      <v-btn icon size="small" variant="text" density="comfortable"
                        @click="setup.Show_SecurityGroupClientSecrete = !setup.Show_SecurityGroupClientSecrete"
                        :aria-label="setup.Show_SecurityGroupClientSecrete ? 'Hide Client Secret' : 'Show Client Secret'">
                        <v-icon size="small">{{ setup.Show_SecurityGroupClientSecrete ? "mdi-eye-outline" :
                          "mdi-eye-off-outline" }}</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="sync-sso-card" aria-label="Sync SSO Groups section"
                    :style="syncSSO ? 'background: #FFF2F2' : 'background: #EDEDED'">
                    <div class="sync-sso-header">
                      <span class="sync-sso-title">Sync SSO Groups</span>
                      <v-tooltip location="top start" max-width="300">
                        <template v-slot:activator="{ props }">
                          <v-icon v-bind="props" size="16" color="grey" class="info-icon"
                            aria-label="Sync SSO Groups information">mdi-information-outline</v-icon>
                        </template>
                        <span>Manually initiates the synchronization process to update local user group memberships
                          based on the
                          current SSO provider data.</span>
                      </v-tooltip>
                    </div>
                    <div class="sync-sso-content">
                      <span class="sync-sso-label">Sync SSO Groups : {{ syncSSO ? 'On' : 'Off' }}</span>
                      <v-switch v-model="syncSSO" hide-details inset density="compact"
                        :class="['sso-toggle', syncSSO ? 'sso-toggle--active' : 'sso-toggle--inactive']"
                        aria-label="Toggle Sync SSO Groups"></v-switch>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-expand-transition>
        </v-card>
        <!-- ...existing other configuration cards with similar modern styling... -->
      </div>
    </v-container>

    <!-- Loading Dialog -->
    <v-dialog v-model="dialog" persistent width="320">
      <v-card class="loading-card">
        <v-card-text class="text-center py-6">
          <v-progress-circular indeterminate color="#EABD07" size="48" class="mb-4"></v-progress-circular>
          <div class="loading-text">Saving configuration...</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// import checkPermissionFour from "@/api/function.js";
import LogTrace from "@/api/function.js";
import { mask } from "vue-the-mask";
import DragDropFile from "@/components/DragDropFile.vue";
import editImg from "@/assets/edit1.png";
import deleteImg from "@/assets/delete1.png";

// Config Card Icons
import gptConfigIcon from "@/assets/Icons/gptModelConfig.svg";
import storageIcon from "@/assets/Icons/storage-config.svg";
import languageIcon from "@/assets/Icons/language-service.svg";
import mailIcon from "@/assets/Icons/mail-config.svg";
import systemIcon from "@/assets/Icons/system-setting.svg";
import otherIcon from "@/assets/Icons/other-config.svg";
import ssoIcon from "@/assets/Icons/single-sign-on.svg";
import microsoftIcon from "@/assets/Icons/microsoft-entra.svg";
import LineIcon from "@/assets/Icons/Line.svg";
import MicrosoftIcon from "@/assets/Icons/Microsoft.svg";
import AuthentikIcon from "@/assets/Icons/Authentik.svg";
import GoogleIcon from "@/assets/Icons/Google.svg";
import AnthropicIcon from "@/assets/Icons/Anthropic.svg";
import TranslatorIcon from "@/assets/Icons/Translator.svg";
import LanguageServiceIcon from "@/assets/Icons/language-service-config.svg";
import SpeechToTextIcon from "@/assets/Icons/speech-to-text.svg";
import AzureBlobStorageIcon from "@/assets/Icons/AzureBlobStorage.svg";
import ImageStorage from "@/assets/Icons/ImageStorage.svg";
import PDFVectorStorage from "@/assets/Icons/PDFVectorStorage.svg";
import QuickReplyIconStorage from "@/assets/Icons/QuickReplyIcon.svg";
import SpeechDocStorage from "@/assets/Icons/SpeechDocStorage.svg";
import deleteIcon from "@/assets/Icons/Delete.png";
import embeddingConfigIcon from "@/assets/Icons/embeddingConfig.svg";
import azureOpenAIIcon from "@/assets/Icons/azure.svg";
import newSaveImg from "@/assets/Icons/white-save-floppy.svg";
import deleteAlertIcon from "@/assets/images/trash-solid.svg";

export default {
  name: "Setup",
  inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
  components: { DragDropFile },
  mixins: [LogTrace],
  directives: {
    mask,
  },
  data: () => ({
    mask: "##########",
    showStickySaveButton: false,
    scrollThreshold: 145,
    items: [],
    panel: [0],
    activeLanguageTab: 0,
    otherTab: 0,
    signOnTab: 0,
    expandedCards: {
      gptConfig: false,
      storageConfig: false,
      languageServices: false,
      mailConfig: false,
      systemSettings: false,
      otherConfig: false,
      singleSignOn: false,
      microsoftEntra: false,
    },

    show1: false,
    formHasErrors: false,
    permission: {
      add: true,
      edit: true,
      delete: true,
    },
    mailcheck: false,
    phonecheck: false,
    filters: {
      username: [],
      email: [],
      employeeName: [],
      userType: [],
    },
    DDRole: [
      {
        name: "Admin",
        value: 1,
      },
      {
        name: "ผู้ใช้งานทั่วไป",
        value: 2,
      },
    ],
    // Mail Config Dropdowns
    emailSendingMethodList: [
      { text: "SMTP", value: "SMTP" },
      { text: "Mailgun", value: "Mailgun" },
    ],
    smtpProviderList: [
      { text: "Office 365 (OAuth2 Recommended)", value: "Office365" },
      { text: "Gmail (App Password)", value: "Gmail" },
      { text: "Other", value: "Other" },
    ],
    smtpAuthMethodList: [
      { text: "Basic Authentication", value: "Basic" },
      { text: "OAuth2 Authentication", value: "OAuth2" },
    ],
    numberRules: [(v) => !!v || "Input is required!"],
    rules: {
      required: (value) => !!value || "Required.",
      min: (v) => v.length >= 10 || "Min 10 numbers",
    },
    Email: null,
    gg: null,
    UserType: null,
    dialog: false,
    edit: {
      id: null,
      key: null,
      value: null,
    },
    user: {
      id: null,
      key: null,
      value: null,
    },

    Editdialog: false,
    Adddialog: false,
    isSearch: false,
    searchname: "",
    email: "",
    totalTable: 0,
    itemsPerPage: 10,
    searchTotalTable: 0,
    options: {},
    today: null,
    setupList: [],
    setupListAzureOpenAIEndpoint: [],
    DDModelShow: [],
    listAzureEndpointFastMode: [],
    setupOpenIDConnectList: [],
    setup: {
      azureOpenAI_URL: null,
      azureOpenAI_APIKey: null,
      azureStorageConnection: null,
      Show_AzureStorageConnection: false,
      azureContainer: null,
      azureStorageConnectionPDFVector: null,
      Show_AzureStorageConnectionPDFVector: false,
      azureContainerPDFVector: null,
      azureStorageConnectionImage: null,
      Show_AzureStorageConnectionImage: false,
      azureContainerImage: null,
      azureStorageSpeech: null,
      Show_AzureStorageSpeech: false,
      azureContainerSpeech: null,
      azureQuickReplyIconStorage: null,
      Show_AzureQuickReplyIconStorage: false,
      azureQuickReplyIconContainer: null,
      languageTranslatorKey: null,
      Show_LanguageTranslatorKey: false,
      languageTranslatorEndpoint: null,
      languageTranslatorLocation: null,
      languageKey: null,
      Show_LanguageKey: false,
      languageEndpoint: null,
      channelSecretInternal: null,
      channelTokenInternal: null,
      // Mail Config - Basic
      emailSendingMethod: null,
      smtpProvider: null,
      smtpAuthMethod: null,
      smtpHost: null,
      smtpPort: null,
      smtpEnableSsl: true,
      smtpTimeout: 30000,
      emailHost: null,
      hostName: null,
      password: null,
      Show_Password: false,
      // Mail Config - OAuth2 (Office 365)
      smtpOAuth2TenantId: null,
      Show_SmtpOAuth2TenantId: false,
      smtpOAuth2ClientId: null,
      Show_SmtpOAuth2ClientId: false,
      smtpOAuth2ClientSecret: null,
      Show_SmtpOAuth2ClientSecret: false,
      // Mail Config - Mailgun
      mailgunApiUrl: null,
      mailgunApiKey: null,
      Show_MailgunApiKey: false,
      mailFrom: null,
      openAIAPIKey: null,
      Show_OpenAI_APIKey: false,
      openAIUrl: null,
      speechToTextKey: null,
      Show_SpeechToTextKey: false,
      speechToTextRegion: null,
      speechToTextLanguage: null,
      pathFFMpeg: null,
      pathFFMpegLocal: null,
      autoStream: null,
      anonymous: null,
      TimeDelayCharacter: null,
      LimitedCharacter: null,
      AzureOpenAIEmbeding_Url: null,
      AzureOpenAIEmbeding_APIKey: null,
      Show_AzureOpenAI_APIKey: false,
      azureOpenAIEmbeding_APIVersion: null,
      AzureOpenAI_EmbedingType: null,
      projectID: null,
      locationID: null,
      fileJSON: null,
      amazonClaude_APIKey: null,
      Show_AmazonClaude_APIKey: false,
      GPTMode: "0",
      GPTRepeatTime: null,
      Model: null,
      Questions: null,
      SSO: null,
      SSOChat: null,
      instance: null,
      domain: null,
      tenantId: null,
      clientId: null,
      callbackPath: null,
      clientSecret: null,
      APIScope: null,
      URLConfig: null,
      SecurityGroupTenantID: null,
      Show_SecurityGroupTenantID: false,
      SecurityGroupClientID: null,
      Show_SecurityGroupClientID: false,
      SecurityGroupClientSecrete: null,
      Show_SecurityGroupClientSecrete: false,
      FlagAutoDisableGPT: null,
      FlagDisableGPT: null,
      AutoDisableGPTPeriodDate: null,
      AutoDisableGPTMessage: null,
      AutoDisableGPTPricing: null,
      EmbLoadBalanceMode: "0",
    },
    secretKey: "01234567890123456789012345678901",
    images: {
      edit: editImg,
      delete: deleteImg,
    },
    azureOpenAI_EmbedingType_List: [
      { text: "text-embedding-3-large", value: "text-embedding-3-large" },
      { text: "text-embedding-3-small", value: "text-embedding-3-small" },
      { text: "text-embedding-ada-002", value: "text-embedding-ada-002" },
    ],
    myClipboard: "",
    myClipboardFullSteam: "",
    urlApi: import.meta.env.VITE_APP_API,
    tempFileJSON: null,
    isUploadFileJSON: false,
    syncSSO: false,
    oldsetup: {
      SecurityGroupTenantID: null,
      SecurityGroupClientID: null,
      SecurityGroupClientSecrete: null,
    },
    imagesIcon: {
      gptConfigIcon: gptConfigIcon,
      storageIcon: storageIcon,
      languageIcon: languageIcon,
      mailIcon: mailIcon,
      systemIcon: systemIcon,
      otherIcon: otherIcon,
      ssoIcon: ssoIcon,
      microsoftIcon: microsoftIcon,
    },
    imagesSSOCard: {
      LineIcon: LineIcon,
      MicrosoftIcon: MicrosoftIcon,
      AuthentikIcon: AuthentikIcon,
    },
    imagesOtherConfigCard: {
      GoogleIcon: GoogleIcon,
      AnthropicIcon: AnthropicIcon,
    },
    imagesLanguageServiceCard: {
      TranslatorIcon: TranslatorIcon,
      LanguageServiceIcon: LanguageServiceIcon,
      SpeechToTextIcon: SpeechToTextIcon,
    },
    imagesStorageConfigCard: {
      AzureBlobStorageIcon: AzureBlobStorageIcon,
      ImageStorage: ImageStorage,
      PDFVectorStorage: PDFVectorStorage,
      QuickReplyIconStorage: QuickReplyIconStorage,
      SpeechDocStorage: SpeechDocStorage,
    },
    deleteIcon: deleteIcon,
    newSaveImg: newSaveImg,
    imagesGPTConfigCard: {
      embeddingConfigIcon: embeddingConfigIcon,
      azureOpenAIIcon: azureOpenAIIcon,
      gptConfigIcon: gptConfigIcon,
    },
  }),
  watch: {
    syncSSO: {
      handler(val) {
        let self = this;
        if (val) {
          // let ConfigData = self.setupOpenIDConnectList.find(
          //   (item) => item.provider == "Microsoft"
          // );
          self.setup.SecurityGroupTenantID = self.oldsetup.SecurityGroupTenantID;
          self.setup.SecurityGroupClientID = self.oldsetup.SecurityGroupClientID;
          self.setup.SecurityGroupClientSecrete = self.oldsetup.SecurityGroupClientSecrete;
        }
      },
      deep: true,
      immediate: true,
    },
    // SMTP Provider watcher - auto-fill host/port when provider changes
    'setup.smtpProvider': {
      handler(val) {
        if (val === 'Office365') {
          this.setup.smtpHost = 'smtp.office365.com';
          this.setup.smtpPort = 587;
          this.setup.smtpEnableSsl = true;
        } else if (val === 'Gmail') {
          this.setup.smtpHost = 'smtp.gmail.com';
          this.setup.smtpPort = 587;
          this.setup.smtpEnableSsl = true;
          this.setup.smtpAuthMethod = 'Basic';
        }
        // 'Other' - keep user input
      },
    },
    // setupOpenIDConnectList: {
    //   handler(val) {
    //     let self = this;
    //     const newItem = val.find((item) => item.provider === "Microsoft");
    //     if (self.syncSSO == true) {
    //       self.setup.SecurityGroupTenantID = newItem.tenantId;
    //       self.setup.SecurityGroupClientID = newItem.clientId;
    //       self.setup.SecurityGroupClientSecrete = newItem.clientSecret;
    //     }
    //   },
    //   deep: true,
    // },
  },
  mounted() {
    this.GetDate();
    this.Get();
    this.GetDDLModelDataAzureProvider();
    this.myClipboard =
      `<script src="${this.urlApi}Snippet/ChatFrameworkSnippet.js"><` +
      "/script>";
    this.myClipboardFullSteam =
      `<script src="${this.urlApi}Snippet/ChatFrameworkSnippetFull.js"><` +
      "/script>";
    const scroller = document.querySelector('.v-main__scroller') || window;
    scroller.addEventListener('scroll', this.handleScroll, true);
    this._scrollContainer = scroller;
  },
  unmounted() {
    // ลบ listener ออกจาก container ที่แท้จริง
    if (this._scrollContainer) {
      this._scrollContainer.removeEventListener('scroll', this.handleScroll, true);
    }
  },

  computed: {
    form() {
      return {
        id: this.user.id,
        key: this.user.key,
        value: this.user.value,
      };
    },
    headers() {
      return [
        {
          title: this.$t("no"),
          align: "center",
          value: "priority",
          sortable: false,
        },
        {
          title: "AzureOpenAIUrl",
          value: "azureOpenAIUrl",
          align: "left",
          sortable: true,
        },
        {
          title: "ResponseTime (Milliseconds)",
          value: "responseTime",
          align: "left",
          sortable: true,
        },
        { title: "Answers", value: "replytext", align: "left", sortable: true },
      ];
    },
  },

  methods: {
    handleScroll(event) {
      const { scrollTop } = event.target;
      const target = event.target;
      if (target.closest('.v-navigation-drawer') ||
        target.closest('.v-app-bar') ||
        target.closest('.v-toolbar')) {
        return;
      }
      if (scrollTop >= this.scrollThreshold) {
        this.showStickySaveButton = true;
      }
      else {
        this.showStickySaveButton = false;
      }
    },
    Apply() {
      try {
        let self = this;
        self.dialog = true;
        self.$call.GetSetupApply(
          (dataResponse) => {
            if (dataResponse.status == 0) {
              // self.Get();
              alert("Apply change");
            }
            self.dialog = false;
          },
          (errorResource) => {
            self.dialog = false;
            if (errorResource.status != 0) {
              console.error(errorResource);
            }
          }
        );
      } catch (error) {
        this.LogTrace(error, "Apply");
      }
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = "0" + dd;
      }
      if (mm < 10) {
        mm = "0" + mm;
      }

      this.today = dd + "/" + mm + "/" + yyyy;
    },
    setListAzureEndpointFastMode(data) {
      let self = this;
      let newList = data.sort((a, b) => a.priority - b.priority);
      let checkfastmode = data.filter((item) => item.responseTime !== null);
      if (checkfastmode.length == newList.length) {
        self.listAzureEndpointFastMode = newList.map((item) => {
          return {
            priority: item.priority,
            azureOpenAIUrl: self.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
            responseTime: item.responseTime == 0 ? "-" : item.responseTime,
            replytext: item.replytext,
          };
        });
      } else {
        self.listAzureEndpointFastMode = [];
      }
    },
    setListOpenIDConnect(data) {
      let self = this;
      const providerOrder = { 'Line': 1, 'Microsoft': 2, 'Authentik': 3 };
      self.setupOpenIDConnectList = data.map((item) => {
        return {
          provider: item.provider,
          sso: item.sso,
          ssoChat: item.ssoChat,
          instance: item.instance,
          domain: item.domain,
          tenantId: item.tenantId,
          Show_TenantId: false,
          clientId: item.clientId,
          Show_ClientId: false,
          callbackPath: item.callbackPath,
          clientSecret: item.clientSecret,
          Show_ClientSecret: false,
          apiScope: item.apiScope,
          Show_APIScope: false,
          urlConfig: item.urlConfig,
        };
      }).sort((a, b) => {
        return (providerOrder[a.provider] || 999) - (providerOrder[b.provider] || 999);
      });
    },
    Get() {
      try {
        let self = this;
        self.$call.GetSetupOverviewGeneral(
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.setupList = dataResponse.data.listDbsettings;
              if (dataResponse.data.listAzureOpenAIEndpoint.length == 0) {
                self.listAzureEndpointFastMode = [];
                self.Add();
              } else {
                self.setupListAzureOpenAIEndpoint =
                  dataResponse.data.listAzureOpenAIEndpoint.map((item) => ({
                    ...item,
                    azureOpenAIUrl: self.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
                    showValue: false, // เพิ่ม key ใหม่ "showValue"
                  }));
                // self.setupListAzureOpenAIEndpoint = dataResponse.data.listAzureOpenAIEndpoint;
                self.setListAzureEndpointFastMode(
                  dataResponse.data.listAzureOpenAIEndpoint
                );
              }

              if (dataResponse.data.listOpenIDConnect.length == 0) {
                self.AddOpenIDConnect("Microsoft");
                self.AddOpenIDConnect("Authentik");
                self.AddOpenIDConnect("Line");
              } else {
                self.setListOpenIDConnect(dataResponse.data.listOpenIDConnect);

                if (
                  self.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Microsoft"
                  ).length == 0
                ) {
                  self.AddOpenIDConnect("Microsoft");
                } else if (
                  self.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Authentik"
                  ).length == 0
                ) {
                  self.AddOpenIDConnect("Authentik");
                } else if (
                  self.setupOpenIDConnectList.filter(
                    (item) => item.provider == "Line"
                  ).length == 0
                ) {
                  self.AddOpenIDConnect("Line");
                }
              }

              self.MapSetupList(self.setupList, false);
            }
            self.dialog = false;
            self.isSearch = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.isSearch = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "Get");
      }
    },
    MapSetupList(source, mapForSave) {
      let self = this;
      source.forEach((element) => {
        switch (element.key) {
          case "AzureContainer":
            element.value = mapForSave
              ? self.setup.azureContainer
              : element.value;
            self.setup.azureContainer = element.value;
            break;
          case "AzureContainerPDFVector":
            element.value = mapForSave
              ? self.setup.azureContainerPDFVector
              : element.value;
            self.setup.azureContainerPDFVector = element.value;
            break;
          case "AzureContainerImage":
            element.value = mapForSave
              ? self.setup.azureContainerImage
              : element.value;
            self.setup.azureContainerImage = element.value;
            break;
          case "AzureOpenAI_APIKey":
            element.value = mapForSave
              ? self.setup.azureOpenAI_APIKey
              : element.value;
            self.setup.azureOpenAI_APIKey = element.value;
            break;
          case "AzureOpenAI_Url":
            element.value = mapForSave
              ? self.setup.azureOpenAI_URL
              : element.value;
            self.setup.azureOpenAI_URL = element.value;
            break;
          case "AzureOpenAIEmbeding_APIKey":
            element.value = mapForSave
              ? self.setup.AzureOpenAIEmbeding_APIKey
              : element.value;
            self.setup.AzureOpenAIEmbeding_APIKey = element.value;
            break;
          case "AzureOpenAIEmbeding_Url":
            element.value = mapForSave
              ? self.setup.AzureOpenAIEmbeding_Url
              : element.value;
            self.setup.AzureOpenAIEmbeding_Url = element.value;
            break;
          case "AzureOpenAIEmbeding_APIVersion":
            element.value = mapForSave
              ? self.setup.AzureOpenAIEmbeding_APIVersion
              : element.value;
            self.setup.AzureOpenAIEmbeding_APIVersion = element.value;
            break;
          case "AzureOpenAI_EmbedingType":
            element.value = mapForSave
              ? self.setup.AzureOpenAI_EmbedingType
              : element.value;
            self.setup.AzureOpenAI_EmbedingType = element.value;
            break;
          case "AzureStorageConnection":
            element.value = mapForSave
              ? self.setup.azureStorageConnection
              : element.value;
            self.setup.azureStorageConnection = element.value;
            break;
          case "AzureStorageConnectionPDFVector":
            element.value = mapForSave
              ? self.setup.azureStorageConnectionPDFVector
              : element.value;
            self.setup.azureStorageConnectionPDFVector = element.value;
            break;
          case "AzureStorageConnectionImage":
            element.value = mapForSave
              ? self.setup.azureStorageConnectionImage
              : element.value;
            self.setup.azureStorageConnectionImage = element.value;
            break;
          case "AzureStorageSpeech":
            element.value = mapForSave
              ? self.setup.azureStorageSpeech
              : element.value;
            self.setup.azureStorageSpeech = element.value;
            break;
          case "AzureContainerSpeech":
            element.value = mapForSave
              ? self.setup.azureContainerSpeech
              : element.value;
            self.setup.azureContainerSpeech = element.value;
            break;
          case "AzureQuickReplyIconStorage":
            element.value = mapForSave
              ? self.setup.azureQuickReplyIconStorage
              : element.value;
            self.setup.azureQuickReplyIconStorage = element.value;
            break;
          case "AzureQuickReplyIconContainer":
            element.value = mapForSave
              ? self.setup.azureQuickReplyIconContainer
              : element.value;
            self.setup.azureQuickReplyIconContainer = element.value;
            break;
          // case "ChannelSecret_Internal":
          //   element.value = mapForSave
          //     ? self.setup.channelSecretInternal
          //     : element.value;
          //   self.setup.channelSecretInternal = element.value;
          //   break;
          // case "ChannelToken_Internal":
          //   element.value = mapForSave
          //     ? self.setup.channelTokenInternal
          //     : element.value;
          //   self.setup.channelTokenInternal = element.value;
          //   break;
          case "EmailHost":
            element.value = mapForSave ? self.setup.emailHost : element.value;
            self.setup.emailHost = element.value;
            break;
          case "HostName":
            element.value = mapForSave ? self.setup.hostName : element.value;
            self.setup.hostName = element.value;
            break;
          case "Password":
            element.value = mapForSave ? self.setup.password : element.value;
            self.setup.password = element.value;
            break;
          // Mail Config - New Fields
          case "EmailSendingMethod":
            element.value = mapForSave ? self.setup.emailSendingMethod : element.value;
            self.setup.emailSendingMethod = element.value;
            break;
          case "SmtpProvider":
            element.value = mapForSave ? self.setup.smtpProvider : element.value;
            self.setup.smtpProvider = element.value;
            break;
          case "SmtpAuthMethod":
            element.value = mapForSave ? self.setup.smtpAuthMethod : element.value;
            self.setup.smtpAuthMethod = element.value;
            break;
          case "SmtpHost":
            element.value = mapForSave ? self.setup.smtpHost : element.value;
            self.setup.smtpHost = element.value;
            break;
          case "SmtpPort":
            if (mapForSave) {
              element.value = self.setup.smtpPort ? String(self.setup.smtpPort) : "587";
            } else {
              self.setup.smtpPort = element.value ? parseInt(element.value) : 587;
            }
            break;
          case "SmtpEnableSsl":
            if (mapForSave) {
              element.value = self.setup.smtpEnableSsl ? "1" : "0";
            } else {
              self.setup.smtpEnableSsl = element.value === true || element.value === "1" || element.value === "true";
            }
            break;
          case "SmtpTimeout":
            if (mapForSave) {
              element.value = self.setup.smtpTimeout ? String(self.setup.smtpTimeout) : "30000";
            } else {
              self.setup.smtpTimeout = element.value ? parseInt(element.value) : 30000;
            }
            break;
          case "SmtpOAuth2TenantId":
            element.value = mapForSave ? self.setup.smtpOAuth2TenantId : element.value;
            self.setup.smtpOAuth2TenantId = element.value;
            break;
          case "SmtpOAuth2ClientId":
            element.value = mapForSave ? self.setup.smtpOAuth2ClientId : element.value;
            self.setup.smtpOAuth2ClientId = element.value;
            break;
          case "SmtpOAuth2ClientSecret":
            element.value = mapForSave ? self.setup.smtpOAuth2ClientSecret : element.value;
            self.setup.smtpOAuth2ClientSecret = element.value;
            break;
          case "MailgunApiUrl":
            element.value = mapForSave ? self.setup.mailgunApiUrl : element.value;
            self.setup.mailgunApiUrl = element.value;
            break;
          case "MailgunApiKey":
            element.value = mapForSave ? self.setup.mailgunApiKey : element.value;
            self.setup.mailgunApiKey = element.value;
            break;
          case "MailFrom":
            element.value = mapForSave ? self.setup.mailFrom : element.value;
            self.setup.mailFrom = element.value;
            break;
          case "LanguageEndpoint":
            element.value = mapForSave
              ? self.setup.languageEndpoint
              : element.value;
            self.setup.languageEndpoint = element.value;
            break;
          case "LanguageKey":
            element.value = mapForSave ? self.setup.languageKey : element.value;
            self.setup.languageKey = element.value;
            break;
          case "LanguageTranslatorEndpoint":
            element.value = mapForSave
              ? self.setup.languageTranslatorEndpoint
              : element.value;
            self.setup.languageTranslatorEndpoint = element.value;
            break;
          case "LanguageTranslatorKey":
            element.value = mapForSave
              ? self.setup.languageTranslatorKey
              : element.value;
            self.setup.languageTranslatorKey = element.value;
            break;
          case "LanguageTranslatorLocation":
            element.value = mapForSave
              ? self.setup.languageTranslatorLocation
              : element.value;
            self.setup.languageTranslatorLocation = element.value;
            break;
          case "Password":
            element.value = mapForSave ? self.setup.password : element.value;
            self.setup.password = element.value;
            break;
          case "OpenAI_Url":
            element.value = mapForSave ? self.setup.openAIUrl : element.value;
            self.setup.openAIUrl = element.value;
            break;
          case "OpenAI_APIKey":
            element.value = mapForSave
              ? self.setup.openAIAPIKey
              : element.value;
            self.setup.openAIAPIKey = element.value;
            break;
          case "SpeechToTextKey":
            element.value = mapForSave
              ? self.setup.speechToTextKey
              : element.value;
            self.setup.speechToTextKey = element.value;
            break;
          case "SpeechToTextRegion":
            element.value = mapForSave
              ? self.setup.speechToTextRegion
              : element.value;
            self.setup.speechToTextRegion = element.value;
            break;
          case "SpeechToTextLanguage":
            element.value = mapForSave
              ? self.setup.speechToTextLanguage
              : element.value;
            self.setup.speechToTextLanguage = element.value;
            break;
          case "PathFFMpeg":
            element.value = mapForSave ? self.setup.pathFFMpeg : element.value;
            self.setup.pathFFMpeg = element.value;
            break;
          case "PathFFMpegLocal":
            element.value = mapForSave
              ? self.setup.pathFFMpegLocal
              : element.value;
            self.setup.pathFFMpegLocal = element.value;
            break;
          case "AutoStream":
            if (mapForSave) {
              element.value = self.setup.autoStream == "1" ? "1" : "0";
            } else {
              self.setup.autoStream = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "Anonymous":
            if (mapForSave) {
              element.value = self.setup.anonymous == "1" ? "1" : "0";
            } else {
              self.setup.anonymous = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "TimeDelayCharacter":
            element.value = mapForSave
              ? self.setup.TimeDelayCharacter
              : element.value;
            self.setup.TimeDelayCharacter = element.value;
            break;
          case "LimitedCharacter":
            element.value = mapForSave
              ? self.setup.LimitedCharacter || "5000"
              : element.value;
            self.setup.LimitedCharacter = element.value;
            break;
          case "FlagAutoDisableGPT":
            if (mapForSave) {
              element.value = self.setup.FlagAutoDisableGPT == "1" ? "1" : "0";
            } else {
              self.setup.FlagAutoDisableGPT = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "FlagDisableGPT":
            if (mapForSave) {
              element.value = self.setup.FlagDisableGPT == "1" ? "1" : "0";
            } else {
              self.setup.FlagDisableGPT = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "AutoDisableGPTPeriodDate":
            element.value = mapForSave
              ? self.setup.AutoDisableGPTPeriodDate
              : element.value;
            self.setup.AutoDisableGPTPeriodDate = element.value;
            break;
          case "AutoDisableGPTMessage":
            element.value = mapForSave
              ? self.setup.AutoDisableGPTMessage
              : element.value;
            self.setup.AutoDisableGPTMessage = element.value;
            break;
          case "AutoDisableGPTPricing":
            element.value = mapForSave
              ? self.setup.AutoDisableGPTPricing
              : element.value;
            self.setup.AutoDisableGPTPricing = element.value;
            break;
          case "ProjectID":
            element.value = mapForSave ? self.setup.projectID : element.value;
            self.setup.projectID = element.value;
            break;
          case "LocationID":
            element.value = mapForSave ? self.setup.locationID : element.value;
            self.setup.locationID = element.value;
            break;
          case "FileJSON":
            element.value = mapForSave ? self.setup.fileJSON : element.value;
            self.setup.fileJSON = element.value;
            if (
              self.setup.fileJSON != null &&
              self.setup.fileJSON != "" &&
              self.setup.fileJSON != undefined
            ) {
              self.isUploadFileJSON = true;
            }
            break;
          case "AmazonClaude_APIKey":
            element.value = mapForSave
              ? self.setup.amazonClaude_APIKey
              : element.value;
            self.setup.amazonClaude_APIKey = element.value;
            break;
          case "GPTMode":
            if (mapForSave) {
              element.value = self.setup.GPTMode == "1" ? "1" : "0";
            } else {
              self.setup.GPTMode = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "GPTRepeatTime":
            element.value = mapForSave
              ? self.setup.GPTMode == "0"
                ? null
                : self.setup.GPTRepeatTime
              : self.setup.GPTMode == "0"
                ? null
                : element.value;
            self.setup.GPTRepeatTime = element.value;
            break;
          case "Model":
            element.value = mapForSave
              ? self.setup.GPTMode == "0"
                ? null
                : self.setup.Model
              : self.setup.GPTMode == "0"
                ? null
                : element.value;
            self.setup.Model = element.value;
            break;
          case "Questions":
            element.value = mapForSave
              ? self.setup.GPTMode == "0"
                ? null
                : self.setup.Questions
              : self.setup.GPTMode == "0"
                ? null
                : element.value;
            self.setup.Questions = element.value;
            break;
          case "SSO":
            if (mapForSave) {
              element.value = self.setup.SSO == "1" ? "1" : "0";
            } else {
              self.setup.SSO = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "SSOChat":
            if (mapForSave) {
              element.value = self.setup.SSOChat == "1" ? "1" : "0";
            } else {
              self.setup.SSOChat = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          case "Instance":
            element.value = mapForSave ? self.setup.instance : element.value;
            self.setup.instance = element.value;
            break;
          case "Domain":
            element.value = mapForSave ? self.setup.domain : element.value;
            self.setup.domain = element.value;
            break;
          case "TenantId":
            element.value = mapForSave ? self.setup.tenantId : element.value;
            self.setup.tenantId = element.value;
            break;
          case "ClientId":
            element.value = mapForSave ? self.setup.clientId : element.value;
            self.setup.clientId = element.value;
            break;
          case "CallbackPath":
            element.value = mapForSave
              ? self.setup.callbackPath
              : element.value;
            self.setup.callbackPath = element.value;
            break;
          case "ClientSecret":
            element.value = mapForSave
              ? self.setup.clientSecret
              : element.value;
            self.setup.clientSecret = element.value;
            break;
          case "APIScope":
            element.value = mapForSave ? self.setup.APIScope : element.value;
            self.setup.APIScope = element.value;
            break;
          case "URLConfig":
            element.value = mapForSave ? self.setup.URLConfig : element.value;
            self.setup.URLConfig = element.value;
            break;
          case "SecurityGroupTenantID":
            element.value = mapForSave
              ? self.setup.SecurityGroupTenantID
              : element.value;
            self.setup.SecurityGroupTenantID = element.value;
            self.oldsetup.SecurityGroupTenantID = self.setup.SecurityGroupTenantID;
            break;
          case "SecurityGroupClientID":
            element.value = mapForSave
              ? self.setup.SecurityGroupClientID
              : element.value;
            self.setup.SecurityGroupClientID = element.value;
            self.oldsetup.SecurityGroupClientID = self.setup.SecurityGroupClientID;
            break;
          case "SecurityGroupClientSecrete":
            element.value = mapForSave
              ? self.setup.SecurityGroupClientSecrete
              : element.value;
            self.setup.SecurityGroupClientSecrete = element.value;
            self.oldsetup.SecurityGroupClientSecrete = self.setup.SecurityGroupClientSecrete;
            break;
          case "SSOGroups":
            if (mapForSave) {
              element.value = self.syncSSO ? "1" : "0";
            } else {
              self.syncSSO = element.value == true || element.value == "1";
            }
            break;
          case "EmbeddingLoadBalanceEnable":
            if (mapForSave) {
              element.value = self.setup.EmbLoadBalanceMode == "1" ? "1" : "0";
            } else {
              self.setup.EmbLoadBalanceMode = element.value == true || element.value == "1" ? "1" : "0";
            }
            break;
          default:
            break;
        }
      });
    },
    EditUser(temp) {
      let self = this;
      self.edit.id = temp.id;
      self.edit.key = temp.key;
      self.edit.value = temp.value;

      self.Editdialog = true;
    },
    normalizeEndpointBaseUrl(value) {
      if (value == null) {
        return value;
      }

      const trimmedValue = String(value).trim();
      if (trimmedValue == "") {
        return trimmedValue;
      }

      const normalizedProtocolValue = trimmedValue
        .replace(/^(https?):\/(?!\/)/i, "$1://")
        .replace(/^https\/\//i, "https://")
        .replace(/^http\/\//i, "http://");
      const valueWithProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(normalizedProtocolValue)
        ? normalizedProtocolValue
        : `https://${normalizedProtocolValue}`;

      try {
        const url = new URL(valueWithProtocol);
        return `${url.protocol}//${url.host}`;
      } catch (error) {
        const match = normalizedProtocolValue.match(/^((?:https?:\/\/)?[^/?#]+)/i);
        return match ? match[1] : trimmedValue;
      }
    },
    NormalizeAzureOpenAIUrl(index) {
      let self = this;
      const item = self.setupListAzureOpenAIEndpoint[index];
      if (!item) {
        return;
      }
      item.azureOpenAIUrl = self.normalizeEndpointBaseUrl(item.azureOpenAIUrl);
    },
    NormalizeAzureOpenAIEndpointList() {
      let self = this;
      self.setupListAzureOpenAIEndpoint = self.setupListAzureOpenAIEndpoint.map((item) => ({
        ...item,
        azureOpenAIUrl: self.normalizeEndpointBaseUrl(item.azureOpenAIUrl),
      }));
    },
    ValidatesetupListAzureOpenAIEndpoint() {
      let self = this;
      if (self.setupListAzureOpenAIEndpoint.length == 0) {
        return false;
      }
      for (let i = 0; i < self.setupListAzureOpenAIEndpoint.length; i++) {
        let item = self.setupListAzureOpenAIEndpoint[i];
        if (
          item.priorityDisplay == 0 ||
          item.priorityDisplay == "" ||
          item.priorityDisplay == null ||
          item.azureOpenAIUrl == "" ||
          item.azureOpenAIUrl == null ||
          item.azureOpenAIAPIKey == "" ||
          item.azureOpenAIAPIKey == null
        ) {
          return false;
        }
      }
      return true;
    },
    ValidateFastMode() {
      let self = this;
      if (self.setup.GPTMode != "1") {
        return true;
      }

      if (
        self.setup.GPTRepeatTime == "0" ||
        self.setup.GPTRepeatTime == 0 ||
        self.setup.GPTRepeatTime == null ||
        self.setup.GPTRepeatTime == undefined
      ) {
        return false;
      } else if (
        self.setup.Model == "" ||
        self.setup.Model == null ||
        self.setup.Model == undefined
      ) {
        return false;
      } else if (
        self.setup.Questions == "" ||
        self.setup.Questions == null ||
        self.setup.Questions == undefined
      ) {
        return false;
      }
      return true;
    },
    ValidateMailConfig() {
      let self = this;
      if (
        self.setup.emailHost == "" ||
        self.setup.emailHost == null ||
        self.setup.emailHost == undefined
      ) {
        return false;
      } else if (
        self.setup.hostName == "" ||
        self.setup.hostName == null ||
        self.setup.hostName == undefined
      ) {
        return false;
      } else if (
        self.setup.smtpPort == "" ||
        self.setup.smtpPort == null ||
        self.setup.smtpPort == undefined
      ) {
        return false;
      } else if (
        self.setup.smtpHost == "" ||
        self.setup.smtpHost == null ||
        self.setup.smtpHost == undefined
      ) {
        return false;
      }
      
      if(self.setup.smtpAuthMethod == 'Basic') {
        if (
          self.setup.password == "" ||
          self.setup.password == null ||
          self.setup.password == undefined
        ) {
          return false;
        }
      } else if(self.setup.smtpAuthMethod == 'OAuth2') {
        if (
          self.setup.smtpOAuth2TenantId == "" ||
          self.setup.smtpOAuth2TenantId == null ||
          self.setup.smtpOAuth2TenantId == undefined
        ) {
          return false;
        } else if (
          self.setup.smtpOAuth2ClientId == "" ||
          self.setup.smtpOAuth2ClientId == null ||
          self.setup.smtpOAuth2ClientId == undefined
        ) {
          return false;
        } else if (
          self.setup.smtpOAuth2ClientSecret == "" ||
          self.setup.smtpOAuth2ClientSecret == null ||
          self.setup.smtpOAuth2ClientSecret == undefined
        ) {
          return false;
        }
      }
      return true;
    },
    Update() {
      try {
        let self = this;
        self.NormalizeAzureOpenAIEndpointList();
        if (
          !self.ValidatesetupListAzureOpenAIEndpoint() ||
          !self.ValidateFastMode()
        ) {
          self.$swal.fire({
            icon: "warning",
            title: self.$t("warning"),
            text: self.$t("warningFillAll"),
            showConfirmButton: false,
            timer: 1800,
          });
          return;
        }
        if(!self.ValidateMailConfig()) {
          self.$swal.fire({
            icon: "warning",
            title: self.$t("warning"),
            text: self.$t("warningEmailConfig"),
            showConfirmButton: false,
            timer: 1800,
          });
          return;
        }
        self.dialog = true;
        self.MapSetupList(self.setupList, true);
        let req = {
          listDbsettings: self.setupList,
          listAzureOpenAIEndpoint: self.setupListAzureOpenAIEndpoint,
          listOpenIDConnect: self.setupOpenIDConnectList,
        };
        self.$call.GetSetupUpdateGeneral(
          req,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.$swal.fire({
                title: "✔ Successfully Saved !",
                showConfirmButton: false,
                timer: 1500,
                customClass: {
                  popup: 'success-banner',
                  title: 'success-banner-title'
                },
              });
              self.Get();
            }
            self.dialog = false;
          },
          (errorResource) => {
            self.dialog = false;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "UpdateUser");
      }
    },
    isNumber: function (evt) {
      evt = evt || window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    numericKey: function (evt) {
      evt = evt ? evt : window.event;
      let expect = evt.target.value.toString() + evt.key.toString();
      if (!/^\d*\.?\d*$/.test(expect) || parseFloat(expect) <= 0) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    copyClipboard() {
      this.$refs.clone.focus();
      document.execCommand("copy");
    },
    copyClipboardFullSteam() {
      this.$refs.cloneFullSteam.focus();
      document.execCommand("copy");
    },
    uploadFile(e) {
      let self = this;
      if (e.length > 0) {
        switch (e[0].type) {
          case "application/json":
            self.tempFileJSON = e[0];
            // console.log("fileJSON", self.tempFileJSON);
            self.readFile(self.tempFileJSON);
            break;
          default:
          // code block
        }
      }
    },
    readFile(file) {
      let self = this;
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = function () {
        self.setup.fileJSON = reader.result;
        self.isUploadFileJSON = false;
        // console.log("self.setup.fileJSON", self.setup.fileJSON);
      };
      reader.onerror = function () {
        alert("Failed to read file!");
      };
    },
    Delete(index) {
      let self = this;
      self.$swal
        .fire({
          title: this.$t("confirmDeletion"),
          text: "Are you sure you want to delete this item? This action cannot be undone.",
          icon: "warning",
          showCloseButton: true,
          showCancelButton: true,
          cancelButtonText: this.$t("cancel"),
          confirmButtonText: `<span class="swal-delete-btn-content"><img src="${deleteAlertIcon}" alt="" class="swal-delete-btn-icon" />${this.$t("delete")}</span>`,
          reverseButtons: false,
          focusCancel: true,
          buttonsStyling: false,
          customClass: {
            popup: "swal-delete-popup",
            title: "swal-delete-title",
            htmlContainer: "swal-delete-body",
            actions: "swal-delete-actions",
            confirmButton: "swal-delete-confirm",
            cancelButton: "swal-delete-cancel",
          },
        })
        .then((result) => {
          if (result.isConfirmed) {
            self.setupListAzureOpenAIEndpoint.splice(index, 1);
          }
        });
    },
    Add() {
      let self = this;
      let temp = {
        id: 0,
        azureOpenAIUrl: null,
        azureOpenAIAPIKey: null,
        priorityDisplay: null,
        showValue: false,
      };
      self.setupListAzureOpenAIEndpoint.push(temp);
    },
    AddOpenIDConnect(Provider) {
      let self = this;
      let instance = null;
      if (Provider == "Microsoft") {
        instance = "https://login.microsoftonline.com/";
      }
      let temp = {
        id: 0,
        provider: Provider,
        sso: false,
        ssoChat: false,
        instance: instance,
        domain: null,
        tenantId: null,
        clientId: null,
        callbackPath: null,
        clientSecret: null,
        apiScope: null,
        urlConfig: null,
      };
      self.setupOpenIDConnectList.push(temp);
    },
    isNumberpositive: function (evt) {
      evt = evt || window.event;
      let charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    async GetDDLModelDataAzureProvider() {
      let self = this;
      self.dialog = true;
      await self.$call.GetDDLModelDataAzureProvider(
        async (dataResponse) => {
          if (dataResponse.status == 0) {
            self.DDModelShow = dataResponse.data;
          } else {
            self.DDModelShow = [];
          }
          self.dialog = false;
        },
        (errorResource) => {
          console.error(errorResource);
        }
      );
    },
  },
};
</script>

<style scoped>
.setup-container {
  /* background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); */
  background: white !important;
  min-height: 100vh;
}

.header-section {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-content {
  color: white;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 300;
  margin: 0;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 24px;
}

.date-display {
  color: white;
  font-size: 1.1rem;
  opacity: 0.9;
}

.save-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white !important;
}

.config-grid {
  /* display: flex;
  flex-direction: column;
  gap: 24px; */
  border-radius: 16px !important;
  border: 1px solid #DCDCDC !important;
  background: white !important;
  overflow: hidden;
  padding: 20px 20px 5px 20px;
}

.config-card {
  border: 1px solid #DCDCDC !important;
  border-radius: 16px !important;
  border: none;
  background: white;
  transition: all 0.3s ease;
  width: 100%;
}

.config-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15) !important;
}

.card-header {
  background: linear-gradient(135deg, #0e81df 30%, #e2e8f0 100%);
  border-radius: 16px 16px 0 0 !important;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.card-header .v-card__title {
  font-size: 1.3rem;
  font-weight: 500;
  color: #495057;
}

.card-content {
  padding: 24px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title-add-btn {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: "";
  width: 4px;
  height: 20px;
  background: #667eea;
  margin-right: 12px;
  border-radius: 2px;
}

.section-title-add-btn::before {
  content: "";
  width: 4px;
  height: 20px;
  background: #667eea;
  margin-right: 12px;
  border-radius: 2px;
}

.section-subtitle {
  font-size: 1rem;
  font-weight: 500;
  color: #6c757d;
  margin-bottom: 16px;
}

.endpoint-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
}

.priority-field {
  max-width: 120px;
}

.toggle-btn,
.delete-btn {
  background: rgba(108, 117, 125, 0.1) !important;
}

.add-endpoint-btn {
  color: #C32E0E !important;
  height: 48px;
  transition: all 0.3s ease;
  border-radius: 16px;
  border: 1px solid #C32E0E !important;
  background: #FFF;
  box-shadow: unset !important;
  text-transform: none !important;
}

.add-endpoint-btn:hover {
  border-color: #C32E0E !important;
  background: rgba(195, 46, 14, 0.05) !important;
}

.storage-section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.storage-section-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.storage-section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.gpt-config-section {
  margin-bottom: 16px;
}

.service-tabs .v-tabs {
  border-radius: 12px;
  background: #f8f9fa;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.setting-item {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
}

.switch-label {
  display: flex;
  align-items: center;
  font-weight: 500;
  color: #495057;
}

.modern-switch .v-input__slot {
  background: transparent !important;
}

.loading-card {
  border-radius: 16px !important;
  text-align: center;
}

.loading-text {
  font-size: 1.1rem;
  color: #495057;
  font-weight: 500;
}

/* Form field styling */
.v-text-field--outlined {
  border-radius: 12px;
}

.v-text-field--outlined .v-input__control .v-input__slot {
  border-radius: 12px;
  transition: all 0.3s ease;
}

.v-text-field--outlined.v-input--is-focused .v-input__control .v-input__slot {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .header-section {
    flex-direction: column;
    text-align: center;
    gap: 24px;
  }

  .header-actions {
    flex-direction: column;
    gap: 16px;
  }

  .page-title {
    font-size: 2rem;
  }
}

@media (max-width: 600px) {
  .setup-container .v-container {
    padding: 16px;
  }

  .header-section {
    padding: 24px;
  }

  .config-card {
    margin-bottom: 16px;
  }

  .card-content {
    padding: 16px;
  }
}

.save-action-card {
  /* background: linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%) !important; */
  border: 1px solid rgba(24, 103, 192, 0.12);
  border-radius: 16px !important;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 0 rgba(24, 103, 192, 0.15) !important;
}

.save-action-content {
  padding: 20px 24px !important;
}

.save-action-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.save-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.save-text {
  display: flex;
  flex-direction: column;
}

.save-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1867c0;
  margin: 0;
  line-height: 1.2;
}

.save-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
  margin: 2px 0 0 0;
  line-height: 1.3;
}

.save-action-button {
  min-width: 200px !important;
  height: 48px !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 16px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.save-action-button:hover {
  transform: translateY(-1px) !important;
  box-shadow: 0 6px 16px rgba(24, 192, 60, 0.35) !important;
}

.save-action-button:active {
  transform: translateY(0) !important;
}

.save-action-button .v-btn__prepend {
  margin-right: 8px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
  .save-action-wrapper {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .save-info {
    justify-content: center;
    text-align: center;
  }

  .save-action-button {
    width: 100%;
    min-width: unset !important;
  }
}

@media (max-width: 600px) {
  .save-action-content {
    padding: 16px 20px !important;
  }

  .save-title {
    font-size: 1rem;
  }

  .save-subtitle {
    font-size: 0.8rem;
  }

  .save-action-button {
    height: 44px !important;
    font-size: 0.9rem !important;
  }

  .save-info .v-icon {
    display: none;
  }
}

/* Animation for better UX */
.save-action-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Loading state for save button */
.save-action-button.v-btn--loading {
  background: linear-gradient(135deg, #2ABF56 0%, #249E48 100%) !important;
}


.sticky-save-btn {
  position: fixed;
  top: 130px;
  right: 30px;
  z-index: 1000;
  max-width: 800px;
  box-shadow: 0px 6px 12px rgba(14, 87, 8, 0.4) !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

/* Animation เมื่อ sticky button ปรากฏ */
.sticky-save-btn {
  animation: slideInFromRight 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
}

@keyframes slideInFromRight {
  from {
    opacity: 0;
    visibility: hidden;
    transform: translateX(100px);
  }

  to {
    opacity: 1;
    visibility: visible;
    transform: translateX(0);
  }
}

/* Enhanced hover effect สำหรับ sticky button */
.sticky-save-btn:hover {
  transform: translateX(-5px) scale(1.02);
  box-shadow: 0px 12px 20px rgba(14, 69, 133, 0.5) !important;
}

/* Responsive สำหรับ sticky button */
@media (max-width: 960px) {
  .sticky-save-btn {
    width: 200px;
    right: 20px;
    top: 120px;
  }
}

@media (max-width: 600px) {
  .sticky-save-btn {
    width: 180px;
    right: 15px;
    top: 100px;
    font-size: 0.9rem !important;
  }
}

/* ซ่อน sticky button เมื่อ dialog เปิด */
.v-overlay--active~.setup-container .sticky-save-btn {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
}
</style>

<!-- Redesign batch css -->
<style scoped>
.image-icon-style {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  flex-shrink: 0;
  object-fit: contain;
}

.entra-field-label {
  margin-bottom: 10px;
}

/* Expandable Card Styles */
.config-card-expandable {
  background: transparent !important;
  border-radius: 16px !important;
  margin-bottom: 1.5rem;
  overflow: hidden;
  border: 1px solid transparent;
  transition: border-color 0.3s ease;
}

.config-card-expanded {
  border: 1px solid #C9C9C9;
}

.expandable-card-header {
  background: linear-gradient(90deg, #FFF2F2 0%, #FFDED8 100%);
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.3s ease;
  user-select: none;
}

.expandable-card-header:hover {
  background: linear-gradient(90deg, #FFE8E8 0%, #FFD0C8 100%);
}

.expandable-card-header:focus {
  /* outline: 2px solid #667eea; */
  outline-offset: 2px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.header-title-core-config {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  letter-spacing: 0.01em;
}

.toggle-icon-btn {
  color: #4a5568 !important;
  transition: transform 0.3s ease;
}

.toggle-icon-btn:hover {
  background: rgba(0, 0, 0, 0.05) !important;
}

/* Card Content when expanded */
.config-card-expandable .card-content {
  background: #ffffff;
  padding: 5px 20px 15px 20px;
  border: 1px solid #f0e6e4;
  border-top: none;
  border-radius: 0 0 16px 16px;
}

/* Transition for expand/collapse */
.v-expand-transition {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active/Expanded state header styling */
.config-card-expandable:has(.card-content:not([style*="display: none"])) .expandable-card-header {
  border-radius: 16px 16px 0 0;
}

/* Accessibility focus styles */
.expandable-card-header:focus-visible {
  outline: 3px solid #667eea;
  outline-offset: 2px;
}

/* Field Label with Info Icon Styles */
.field-label-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.field-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required-asterisk {
  color: #dc2626;
  font-weight: 600;
}

.info-icon {
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.info-icon:hover {
  opacity: 1;
}

/* Sync SSO Groups Card Styles */
.sync-sso-card {
  border-radius: 16px;
  padding: 10px 20px;
  height: 100%;
  max-width: 420px !important;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.sync-sso-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.sync-sso-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
}

.sync-sso-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sync-sso-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
}

/* SSO Toggle Switch Styles */
.sso-toggle {
  margin: 0 4px;
}

.sso-toggle :deep(.v-switch__track) {
  transform: scale(0.8);
  width: 40px;
  height: 28px;
  border-radius: 999px;
  border: 2px solid transparent;
  background-color: transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.sso-toggle :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
  box-shadow: none;
  border: 2px solid transparent;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.sso-toggle--active :deep(.v-switch__track) {
  background-color: #ffffff !important;
  border-color: #d32f2f;
}

.sso-toggle--active :deep(.v-switch__thumb) {
  background-color: #d32f2f !important;
  border-color: #d32f2f;
  transform: translateX(-0.5px);
}

.sso-toggle--inactive :deep(.v-switch__track) {
  background-color: #bdbdbd !important;
  border-color: #bdbdbd;
}

.sso-toggle--inactive :deep(.v-switch__thumb) {
  background-color: #ffffff !important;
  border-color: #bdbdbd;
  width: 30px;
  height: 30px;
}

/* SSO Tabs Container Styles */
.sso-tabs-container {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 8px 0;
  border-bottom: 2px solid #e5e7eb;
}

.sso-tab-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.2s ease;
  position: relative;
}

.sso-tab-item:hover {
  background: rgba(0, 0, 0, 0.03);
}

.sso-tab-item:focus {
  /* outline: 2px solid #dc2626; */
  outline-offset: 2px;
}

.sso-tab-item::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 3px;
  background: transparent;
  border-radius: 2px 2px 0 0;
  transition: background 0.2s ease;
}

.sso-tab-item.sso-tab-active::after {
  background: #dc2626;
}

.sso-tab-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  transition: all 0.2s ease;
  border-color: unset !important;
}

.sso-tab-icon-wrapper.sso-tab-icon-active {
  background-color: #dc2626;
  border-color: unset !important;
}

.sso-tab-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  transition: filter 0.2s ease;
}

.sso-tab-icon.sso-tab-icon-white {
  filter: brightness(0) invert(1);
}

.sso-tab-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #4b5563;
  transition: color 0.2s ease;
}

.sso-tab-label.sso-tab-label-active {
  color: #dc2626;
  font-weight: 600;
}

/* table base */
:deep(thead) {
  background-color: #FFF2F2 !important;
  padding: 0 8px;
}

.v-table--has-top :deep(.v-table__wrapper) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-left .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}

:deep(.v-data-table .v-table__wrapper > table > thead > tr th.v-data-table-column--align-center .v-data-table-header__content span) {
  font-weight: 600;
  font-size: 14px;
}
</style>
